import { Component, ElementRef, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, timeout } from 'rxjs/operators';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { createDimensionSelection } from '../../../accounting/model/const/enum';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlValidators } from '../../../core/component/forms/form-control.validators';
import { SelectionItem } from '../../../core/component/select/select.component';
import { AstralTab } from '../../../core/component/astral-components/astral-tabs/astral-tabs.component';
import { getMonthName } from '../../../core/helper/month';
import { numberValue } from '../../../core/helper/number-value';
import { getTranslation } from '../../../core/helper/translation';
import { Command, standardCommand } from '../../../core/model/command';
import { Image } from '../../../core/model/image';
import { Currency, CurrencySymbol } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FileService } from '../../../core/service/file.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { NonResidentialProperty } from '../../../overview/model/non-residential-property';
import { ResidentialProperty } from '../../../overview/model/residential-property';
import { companyCommand } from '../../command/company.command';
import {
  AccountingExportDateLimit,
  AccountingExportMethodKind,
  AccountingFileFormat,
  Company,
  CompanyForm,
  CompanyForms,
  CompanyNameplateType,
  Gl07ExportConfiguration,
  RaindanceExportConfiguration,
  VismaRorExportConfiguration,
} from '../../model/company';
import { CompanyCostBearer, CompanyCostPlace } from '../../model/company-cost-place';
import { CompanyBankAccount } from '../../model/company-bank-account';
import { CompanyCustomProperty } from '../../model/company-custom-property';
import { DebtCollectionConfiguration } from '../../model/debt-collection-configuration';
import {
  CustomPropertyDataType,
  DebtCollectionSupplierKind,
  DebtCollectionSupplierKindToString,
  RentalObjectCategory,
} from '../../model/const/enums';
import { ContactIdKind } from '../../model/contact';
import { CompanyFormCommandHandlerResolver } from '../../service/command/company-form-command-handler-resolver.service';
import { CompanyFortnoxConfigCommandHandler } from '../../service/command/company-fortnox-config-command-handler.service';
import { CompanyVismaConfigCommandHandler } from '../../service/command/company-visma-config-command-handler.service';
import { ArticleService } from '../../service/data/article.service';
import { CompanyAvyIntegrationSettings, CompanyService, FailedToLoadLogoSrc } from '../../service/data/company.service';
import { CompanyLogService } from '../../service/data/company-log.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { DwellingService } from '../../service/data/dwelling.service';
import { OtherRentalObjectService } from '../../service/data/other-rental-object.service';
import { ParkingService } from '../../service/data/parking.service';
import { PremisesService } from '../../service/data/premises.service';
import { CompanyDocumentComponent } from '../company-document/company-document.component';
import { CompanyBankAccountsComponent } from '../company-bank-account/company-bank-accounts.component';
import { LanguageService } from '../../../core/service/language.service';
import { LocalDate } from '../../../core/model/local-date';
import { DwellingInspectionTypeService } from '../../../dwelling-inspection/service/data/dwelling-inspection-type.service';
import { DwellingInspectionType } from '../../../dwelling-inspection/model/dwelling-inspection-type';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CompanyHogiaConfigComponent } from '../company-hogia-config/company-hogia-config.component';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { LicenseService } from '../../../common/service/data/license.service';
import { LicenseTypeKind } from '../../../common/model/const/enum';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { SetLockDateComponent } from '../set-lock-date/set-lock-date.component';
import { AccountingPeriodService } from '../../../accounting/service/data/accounting-period.service';
import { OpeningBalanceRun } from '../../../accounting/model/opening-balance-run';
import {
  AllowedOnlineTerminationSignatureKind,
  CompanyAutomaticAdvicingSettings,
  CompanyExternalAccountsReceivableSettings,
  CompanyFaultNotificationSettings,
  CompanyInvoiceReminderSettings,
  CompanyRentSettings,
  CompanySystemSettings,
  CompanyTerminationSettings,
  CompanyTenantPagesSettings,
  GetReminderDeliveryMethodIcon,
  ReminderDeliveryMethod,
  TenantPageSource,
} from '../../model/company-system-settings';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { LogRecord } from '../../../common/model/log-record';

export function typesValidator(mandatoryTypeControl: AbstractControl): ValidatorFn {
  return (optionalTypeControl: AbstractControl): ValidationErrors | null => {
    if (!mandatoryTypeControl || !optionalTypeControl) return null;

    const selectedMandatoryType = mandatoryTypeControl.value;
    const selectedOptionalType = optionalTypeControl.value;

    if (!selectedMandatoryType || selectedMandatoryType !== selectedOptionalType) return null;

    return { dwellingInspectionTypeOptionalId: { message: 'COMPANY.DEFAULT_INSPECTION_TYPES_VALIDATOR' } };
  };
}

type CompanySystemSettingsSection =
  | 'invoiceReminder'
  | 'automaticAdvicing'
  | 'faultNotification'
  | 'tenantPages'
  | 'termination'
  | 'rent'
  | 'externalAccountsReceivable';
const IMD_FTP_PASSWORD_PLACEHOLDER = '********';

@Component({
  selector: 'app-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
  providers: [
    DeleteFormCommandHandler,
    CompanyFormCommandHandlerResolver,
    CompanyFortnoxConfigCommandHandler,
    CompanyVismaConfigCommandHandler,
    DwellingInspectionTypeService,
    AccountingPeriodService,
    { provide: DataService, useExisting: CompanyService },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyFormComponent extends FormComponentBase<Company, CompanyService> {
  constructor(
    dataService: CompanyService,
    private _cf: FormControlExFactory,
    private _fb: UntypedFormBuilder,
    private _fileService: FileService,
    private _sanitizer: DomSanitizer,
    private dwellingService: DwellingService,
    private otherRenalObjectService: OtherRentalObjectService,
    private parkingService: ParkingService,
    private premisesService: PremisesService,
    private articleService: ArticleService,
    private accountPlanSerive: AccountPlanService,
    private dwellingInspectionTypeService: DwellingInspectionTypeService,
    private _languageService: LanguageService,
    private _messageService: MessageService,
    private _actionService: ActionWindowService,
    private _injector: Injector,
    private _nativeWindow: NativeWindowService,
    private _licenseService: LicenseService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _userAccountService: UserAccountService,
    private readonly _companyLogService: CompanyLogService,
    columnDefinitionService: ColumnDefinitionService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CompanyFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, dataService, messageService, navigation, commandDispatcher, handlerResolver);

    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.countries = this.dataService.getBusinessCountryCultureSelection();
    this.logColumnDefinition = columnDefinitionService.create('Log');

    this.closeAfterDaysSelection = [new SelectionItem('COMMON.DEACTIVATED', null)];
    for (let day = 1; day <= 30; day++) {
      this.closeAfterDaysSelection.push(new SelectionItem(day.toString(), day));
    }

    this.sendAccountingDaySelection = [new SelectionItem('COMMON.DAILY', 0)];
    for (let day = 1; day <= 31; day++) {
      this.sendAccountingDaySelection.push(new SelectionItem(day.toString(), day));
    }
  }

  logs: LogRecord[] = [];
  logColumnDefinition: ColumnDefinition[];

  override initializeSection(section: string): void {
    if (section === 'logs' && this.dataContext?.id) {
      this.AddSubscription(
        this._companyLogService.getPagedList(this.dataContext.id, 1, 500).subscribe(result => {
          this.logs = result.items;
        })
      );
    }

    super.initializeSection(section);
  }

  @ViewChild('fileInput') uploadFileElement: ElementRef;
  @ViewChild(CompanyDocumentComponent) documentComponent: CompanyDocumentComponent;
  @ViewChild(CompanyBankAccountsComponent) bankAccountsComponent: CompanyBankAccountsComponent;

  dwelling: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Dwelling });
  dwellingExist: boolean;
  otherRentalObject: NonResidentialProperty = new NonResidentialProperty({ category: RentalObjectCategory.Other });
  otherRentalObjectExist: boolean;
  parking: NonResidentialProperty = new NonResidentialProperty({ category: RentalObjectCategory.Parking });
  parkingExist: boolean;
  premises: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Premises });
  premisesExist: boolean;
  overviewInitialized = false;
  companyStatisticsLoaded = false;
  eInvoice = false;
  articles: any[];
  amortizationPlanArticles: any[];
  invoiceFeeArticles: any[];
  showImdFtp: boolean;
  countries: SelectionItem[];
  accountYearMonths: { name: string; value: number }[];
  companyForms: SelectionItem[];
  dwellingInspectionTypesSelection: SelectionItem[];
  private defaultInspectionType: DwellingInspectionType;
  hasKivraLicense = false;
  systemSettings: CompanySystemSettings;
  systemSettingsForm: FormGroup;
  systemSettingsSidebarOpen = false;
  systemSettingsSidebarSection: CompanySystemSettingsSection | null = null;
  avyIntegrationForm: FormGroup;
  private avyIntegrationFormSubscription: Subscription;
  private avyIntegrationCompanyId: string | null = null;
  private readonly avyIntegrationSavePendingCompanyIds = new Set<string>();
  avyIntegrationSidebarOpen = false;
  accountPlanSidebarOpen = false;
  reminderDeliveryMethodFields: { caption: string; property: keyof CompanyInvoiceReminderSettings }[] = [
    { caption: 'E-post med PDF', property: 'reminderDeliveryMethodEmailWithPDF' },
    { caption: 'E-post utan PDF', property: 'reminderDeliveryMethodEmailWithoutPDF' },
    { caption: 'Brev', property: 'reminderDeliveryMethodMail' },
    { caption: 'Manuell', property: 'reminderDeliveryMethodManual' },
    { caption: 'E-faktura B2B', property: 'reminderDeliveryMethodEInvoiceB2B' },
    { caption: 'E-faktura B2C', property: 'reminderDeliveryMethodEInvoiceB2C' },
    { caption: 'Avy', property: 'reminderDeliveryMethodAvy' },
    { caption: 'Kivra', property: 'reminderDeliveryMethodKivra' },
    { caption: 'Ingen leverans', property: 'reminderDeliveryMethodNoDelivery' },
    { caption: 'SMS', property: 'reminderDeliveryMethodSMS' },
  ];
  tenantTerminationFields = [
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.DWELLING',
      printProperty: 'allowPrintTerminationFormDwelling',
      onlineProperty: 'allowTerminationOnlineDwelling',
    },
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.PREMISES',
      printProperty: 'allowPrintTerminationFormPremises',
      onlineProperty: 'allowTerminationOnlinePremises',
    },
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.PARKING',
      printProperty: 'allowPrintTerminationFormParking',
      onlineProperty: 'allowTerminationOnlineParking',
    },
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.OTHER',
      printProperty: 'allowPrintTerminationFormOther',
      onlineProperty: 'allowTerminationOnlineOther',
    },
  ];
  reminderDeliveryMethodSelection = [
    new SelectionItem('Ingen', ReminderDeliveryMethod.None, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.None)),
    new SelectionItem('E-post', ReminderDeliveryMethod.Email, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Email)),
    new SelectionItem(
      'Skriv ut själv',
      ReminderDeliveryMethod.Manual,
      null,
      null,
      GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Manual)
    ),
    new SelectionItem('Brev', ReminderDeliveryMethod.Mail, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Mail)),
  ];
  tenantPageSourceSelection = [
    new SelectionItem('Inbyggd', TenantPageSource.BuiltIn),
    new SelectionItem('Extern', TenantPageSource.External),
  ];
  onlineTerminationSignatureSelection = [
    new SelectionItem('Endast BankID', AllowedOnlineTerminationSignatureKind.OnlyBankID),
    new SelectionItem('BankID och klicksignering', AllowedOnlineTerminationSignatureKind.BankIDAndClickSign),
  ];
  nameplateEnabled: boolean;
  defaultNameplate: string;
  defaultNameplateOneLine: string;
  defaultNameplateShortMulti: string;
  defaultNameplateOneLineShort: string;
  canCloseAccountingPeriod = false;
  canReopenAccountingPeriod = false;

  get logoIsBroken(): boolean {
    return this.dataContext?.logoSrc === FailedToLoadLogoSrc;
  }

  get logoUrl(): SafeUrl {
    if (this.form.controls['logo'].value instanceof Image) {
      return this._sanitizer.bypassSecurityTrustUrl(this.form.controls['logo'].value.url);
    }
    return null;
  }

  get accountingClosedToText(): string {
    return this.dataContext?.accountingClosedTo?.toLocaleDateString(this._languageService.currentCultureLang) ?? '';
  }

  get hasClosedAccountingPeriod(): boolean {
    return LocalDate.fromDate(this.dataContext?.accountingClosedTo)?.hasValue() ?? false;
  }

  counterPartUsed: boolean = false;

  showRopo: boolean; // Ropo is the old API to collect debt cases
  showRopoOne: boolean; // RopoOne is Ropos new API to collect debt cases

  get rentalObjectsExist(): boolean {
    return this.dwellingExist || this.parkingExist || this.otherRentalObjectExist || this.premisesExist;
  }

  get showOverviewSection(): boolean {
    return !!this.dataContext && !this.isNew && (!this.companyStatisticsLoaded || this.rentalObjectsExist);
  }

  private _initAccountYearMonths(translations: any) {
    this.accountYearMonths = [
      { name: `${getTranslation(translations, getMonthName(1))} - ${getTranslation(translations, getMonthName(12))}`, value: 1 },
      { name: `${getTranslation(translations, getMonthName(2))} - ${getTranslation(translations, getMonthName(1))}`, value: 2 },
      { name: `${getTranslation(translations, getMonthName(3))} - ${getTranslation(translations, getMonthName(2))}`, value: 3 },
      { name: `${getTranslation(translations, getMonthName(4))} - ${getTranslation(translations, getMonthName(3))}`, value: 4 },
      { name: `${getTranslation(translations, getMonthName(5))} - ${getTranslation(translations, getMonthName(4))}`, value: 5 },
      { name: `${getTranslation(translations, getMonthName(6))} - ${getTranslation(translations, getMonthName(5))}`, value: 6 },
      { name: `${getTranslation(translations, getMonthName(7))} - ${getTranslation(translations, getMonthName(6))}`, value: 7 },
      { name: `${getTranslation(translations, getMonthName(8))} - ${getTranslation(translations, getMonthName(7))}`, value: 8 },
      { name: `${getTranslation(translations, getMonthName(9))} - ${getTranslation(translations, getMonthName(8))}`, value: 9 },
      { name: `${getTranslation(translations, getMonthName(10))} - ${getTranslation(translations, getMonthName(9))}`, value: 10 },
      { name: `${getTranslation(translations, getMonthName(11))} - ${getTranslation(translations, getMonthName(10))}`, value: 11 },
      { name: `${getTranslation(translations, getMonthName(12))} - ${getTranslation(translations, getMonthName(11))}`, value: 12 },
    ];
  }

  private _validateNextInvoiceNo(control: AbstractControl): ValidationErrors {
    if (!/^([1-9]\d{0,8})$/.test(control.value)) {
      return { nextInvoiceNo: { message: 'COMPANY.NEXT_INVOICE_NUMBER_ERROR' } };
    }
    return null;
  }

  closeAfterDaysSelection: SelectionItem[] = [];

  currencies = [
    { name: CurrencySymbol(Currency.SEK), value: Currency.SEK },
    { name: CurrencySymbol(Currency.DKK), value: Currency.DKK },
    { name: CurrencySymbol(Currency.EUR), value: Currency.EUR },
  ];

  invoicingIntervals = [
    { name: 'CONST.TIME_UNIT_MONTH', value: 1 },
    { name: 'CONST.TIME_UNIT_TWO_MONTHS', value: 2 },
    { name: 'CONST.TIME_UNIT_QUATER', value: 3 },
  ];

  useEarlyTerminations = [
    { name: 'CONST.USEEARLYTERMINATIONTYPES.NOT_USED', value: 0 },
    { name: 'CONST.USEEARLYTERMINATIONTYPES.USED_DEFAULT_VALUE_YES', value: 1 },
    { name: 'CONST.USEEARLYTERMINATIONTYPES.USED_DEFAULT_VALUE_NO', value: 2 },
  ];

  get showBrf(): boolean {
    let companyForm = this.form.controls['companyForm'].value;
    return companyForm === CompanyForm.SE_BostadsrattsForening || companyForm === CompanyForm.SE_OaktaBostadsrattsForening;
  }

  get idType(): ContactIdKind {
    if (!this.form || !this.form.controls['cultureName'].value) {
      return ContactIdKind.SwedishOrganizationNumber;
    }
    switch (this.form.controls['cultureName'].value) {
      case 'da-DK':
        return ContactIdKind.DanishOrganizationNumber;
      case 'fi-FI':
        return ContactIdKind.FinnishOrganizationNumber;
      default:
        return ContactIdKind.SwedishOrganizationNumber;
    }
  }

  protected getFormGroup(): FormGroup {
    // Older companies can have a NULL legacy database value. Object.assign in
    // DataService otherwise replaces the model constructor's default with null.
    this.dataContext.debtCollectionConfiguration ??= new DebtCollectionConfiguration();

    for (let key in this.dataContext.ftpFileNamingConventionSelection) {
      this.ftpFileNameConventionSelection.push(new SelectionItem(this.dataContext.ftpFileNamingConventionSelection[key], key + ''));
      // Use `key` and `value`
    }

    this.counterPartUsed = this.dataContext.useCounterpart;

    let group = this._fb.group({
      address: this._fb.group({}),
      bankgiroNumber: this._cf.createMaxLength('COMPANY.BANK_GIRO', this.dataContext.bankgiroNumber, 9),
      email: this._cf.createWithOptions('COMPANY.EMAIL', this.dataContext.email, {
        length: 255,
        required: true,
        validator: Validators.email,
      }),
      firstMonthInAccountingYear: this._cf.create(
        'COMPANY.FIRST_MONTH_IN_ACCOUNTING_YEAR',
        this.dataContext.firstMonthInAccountingYear ? this.dataContext.firstMonthInAccountingYear : 1
      ),
      logo: this._cf.createHidden(this.dataContext.logo),
      name: this._cf.createWithOptions('COMPANY.NAME', this.dataContext.name, { length: 60, required: true }),
      nextInvoiceNumber: this._cf.createWithOptions('COMPANY.NEXT_INVOICE_NO', this.dataContext.nextInvoiceNumber, {
        length: 9,
        isReadonly: !this.dataContext.allowChangeNextInvoiceNumber,
        validator: control => this._validateNextInvoiceNo(control),
      }),
      number: this._cf.createMandatory('COMPANY.CODE', this.dataContext.number),
      organizationNo: this._cf.createMandatory('COMPANY.ORGANIZATION_NUMBER', this.dataContext.organizationNo),
      vatNumber: this._cf.create('COMPANY.VAT_NUMBER', this.dataContext.vatNumber),
      paymentReceiver: this._cf.create('COMPANY.PAYMENT_RECIEVER', this.dataContext.paymentReceiver),
      issuerIdentity: this._cf.create('COMPANY.PAYMENT_RECIEVER', this.dataContext.issuerIdentity),
      phone: this._cf.createMaxLength('COMPANY.PHONE', this.dataContext.phone, 20),
      verificationNumberSeriesInvoicing: this._cf.createMaxLength(
        'COMPANY.VER_NO_SERIES_INVOICING',
        this.dataContext.verificationNumberSeriesInvoicing,
        10
      ),
      verificationNumberSeriesGeneral: this._cf.createMaxLength(
        'COMPANY.VER_NO_SERIES_GENERAL',
        this.dataContext.verificationNumberSeriesGeneral,
        10
      ),
      ftpFilePrefix: this._cf.create(
        'COMPANY.FTP_FILE_PREFIX',
        this.dataContext.ftpFilePrefix,
        Validators.maxLength(50),
        null,
        'COMPANY.HELP_TEXT.FTP_FILE_PREFIX'
      ),
      counterpart: this._cf.create('COMMON.COUNTERPART', this.dataContext.counterpart, null, null, 'COMPANY.HELP_TEXT.COUNTERPART'),
      imdFtpEnabled: this._cf.create('COMPANY.IMD_FTP_ENABLED', this.dataContext.imdFtpEnabled),
      imdFtpAddress: this._cf.createReadonly('COMPANY.IMD_FTP_ADDRESS', this.dataContext.imdFtpServerAddress),
      imdFtpPort: this._cf.createReadonly('COMPANY.IMD_FTP_PORT', this.dataContext.imdFtpServerPort),
      imdFtpUser: this._cf.createReadonly('COMPANY.IMD_FTP_USERNAME', this.dataContext.imdFtpUser),
      imdFtpPassword: this._cf.create(
        'COMPANY.IMD_FTP_PASSWORD',
        this.dataContext.imdFtpEnabled ? IMD_FTP_PASSWORD_PLACEHOLDER : ''
      ),
      imdFtpChargeNegativeValues: this._cf.create('COMPANY.IMD_FTP_CHARGE_NEGATIVE_VALUES', this.dataContext.imdFtpChargeNegativeValues),
      invoiceFooter: this._cf.create('COMPANY.INVOICE_FOOTER', this.dataContext.invoiceFooter),
      eInvoiceAddress: this._cf.create('TENANT.EINVOICE_ADDRESS', this.dataContext.eInvoiceAddress),
      eInvoiceSpecified: this._cf.create('TENANT.EINVOICE_SPECIFIED', this.dataContext.eInvoiceSpecified),
      isKivraIntegrationEnabled: this._cf.create('TENANT.KIVRA_SPECIFIED', this.dataContext.kivraIntegration.enabled),
      specialTermsDwelling: this._cf.createMaxLength('COMPANY.SPECIAL_TERMS_DWELLING', this.dataContext.specialTermsDwelling, 3000),
      specialTermsParking: this._cf.createMaxLength('COMPANY.SPECIAL_TERMS_PARKING', this.dataContext.specialTermsParking, 3000),
      generalTermsDwelling: this._cf.createMaxLength('COMPANY.GENERAL_TERMS_DWELLING', this.dataContext.generalTermsDwelling, 4000),
      generalTermsParking: this._cf.createMaxLength('COMPANY.GENERAL_TERMS_PARKING', this.dataContext.generalTermsParking, 4000),
      rentPaymentTermsDwelling: this._cf.createMaxLength(
        'COMPANY.RENT_PAYMENT_TERMS_DWELLING',
        this.dataContext.rentPaymentTermsDwelling,
        1000
      ),
      rentPaymentTermsParking: this._cf.createMaxLength(
        'COMPANY.RENT_PAYMENT_TERMS_PARKING',
        this.dataContext.rentPaymentTermsParking,
        1000
      ),
      currencyCode: this._cf.createWithOptions('COMMON.CURRENCY', this.dataContext.currencyCode, {
        required: true,
        isReadonly: !this.dataContext.allowChangeCurrency,
      }),
      companyForm: this._cf.create('COMPANY.COMPANY_FORM', this.dataContext.companyForm),
      articleId: this._cf.create('COMPANY.ARTICLE_REMINDER_FEE', this.dataContext.articleId),
      totalShare: this._cf.create('COOPERATIVE_HOUSING.TOTAL_SHARE', this.dataContext.totalShare),
      accountingClosedTo: this._fb.group({}),
      closeAccountingAfterDays: this._cf.create(
        'COMPANY.CLOSE_ACCOUNTING_AFTER_DAYS',
        this.dataContext.closeAccountingAfterDays,
        null,
        null,
        'COMPANY.HELP_TEXT.CLOSE_ACCOUNTING_AFTER_DAYS'
      ),
      economicalPlanSent: this._fb.group({}),
      economicalPlanApproved: this._fb.group({}),
      accountPlanId: this._cf.createMandatory('ACCOUNT_PLAN.ACCOUNT_PLAN', this.dataContext.accountPlanId),
      debtCollectionConfiguration: this.createDebtCollectionForm(),
      cultureName: this._cf.createMandatory('COMPANY.ACTIVE_IN_COUNTRY', this.dataContext.cultureName),
      onlineTerminationDwellingInspection: this._cf.create(
        'COMPANY.DEFAULT_INSPECTION_TYPES_INFO',
        this.dataContext.onlineTerminationDwellingInspection
      ),
      dwellingInspectionTypeMainId: this._cf.create('COMPANY.DWELLING_INSPECTION_TYPE_MAIN', this.dataContext.dwellingInspectionTypeMainId),
      dwellingInspectionTypeOptionalId: this._cf.create(
        'COMPANY.DWELLING_INSPECTION_TYPE_OPTIONAL',
        this.dataContext.dwellingInspectionTypeOptionalId
      ),
      enableFaultNotificationDialog: this._cf.create(
        'COMPANY.ACTIVATE_FAULT_NOTIFICATION_DIALOG',
        this.dataContext.enableFaultNotificationDialog
      ),
      enableFaultNotificationReporting: this._cf.create(
        'COMPANY.ACTIVATE_FAULT_NOTIFICATION_REPORTING',
        this.dataContext.enableFaultNotificationReporting
      ),
      amortizationPlanEnabled: this._cf.create(
        'COMPANY.INTEGRATION.ALLOW_AMORTIZATION_PLAN',
        this.dataContext.amortizationPlanIntegration.enabled
      ),
      amortizationPlanLetterText: this._cf.createMaxLength(
        'COMPANY.INTEGRATION.AMORTIZATION_PLAN_LETTER_TEXT',
        this.dataContext.amortizationPlanIntegration.letterText,
        8000
      ),
      amortizationPlanDefaultArticleId: this._cf.create(
        'COMPANY.INTEGRATION.AMORTIZATION_PLAN_DEFAULT_ARTICLE',
        this.dataContext.amortizationPlanIntegration.defaultArticleId
      ),
      dinBoxEnabled: this._cf.create('COMPANY.INTEGRATION.USE_DIN_BOX', this.dataContext.dinBoxIntegration.enabled),
      dinBoxUserName: this._cf.create('COMPANY.INTEGRATION.DIN_BOX_USERNAME', this.dataContext.dinBoxIntegration.userName),
      dinBoxPassword: this._cf.create('COMPANY.INTEGRATION.DIN_BOX_PASSWORD', this.dataContext.dinBoxIntegration.password),
      invoicingBankAccountId: this._cf.create('COMPANY.DEFAULT_INVOICE_ACCOUNT', this.dataContext.invoicingBankAccountId),
      nameplateEnabled: this._cf.create('NAMEPLATE.SHOW_SETTINGS', this.dataContext.nameplateType != CompanyNameplateType.Auto),
      nameplateOneline: this._cf.create(
        'NAMEPLATE.ONELINE',
        this.dataContext.nameplateType == CompanyNameplateType.Oneline ||
          this.dataContext.nameplateType == CompanyNameplateType.OnelineShort
      ),
      nameplateShortNames: this._cf.create(
        'NAMEPLATE.SHORT',
        this.dataContext.nameplateType == CompanyNameplateType.MultilineShort ||
          this.dataContext.nameplateType == CompanyNameplateType.OnelineShort
      ),
      defaultNameplate: this._cf.createReadonly('NAMEPLATE.LABEL', this.defaultNameplate),
      companyCostPlaces: this._fb.array(this.dataContext.companyCostPlaces.map(item => this._createCompanyCostPlaceGroup(item))),
      companyCostBearers: this._fb.array(this.dataContext.companyCostBearers.map(item => this._createCompanyCostBearerGroup(item))),
      companyCustomProperties: this._createPropertiesForm(this.dataContext.companyCustomProperties),
      imdOffset: this._cf.create(this.getImdOffsetText(this.dataContext), this.dataContext.imdOffset),
      invoicingInterval: this._cf.createMandatory(
        'COMPANY.INVOICING_INTERVAL',
        this.dataContext.invoicingInterval,
        'COMPANY.HELP_TEXT.INVOICING_INTERVAL'
      ),
      useEarlyTermination: this._cf.create(
        'COMPANY.USE_EARLY_TERMINATION',
        this.dataContext.useEarlyTermination,
        null,
        null,
        'COMPANY.HELP_TEXT.USE_EARLY_TERMINATION'
      ),
      invoiceFeeArticleId_Mail: this._cf.create('COMPANY.INVOICE_FEE.MAIL_ACCOUNT', this.dataContext.invoiceFeeArticleId_Mail),
      invoiceFeeAmount_Mail: this._cf.create('COMMON.AMOUNT', this.dataContext.invoiceFeeAmount_Mail),
      invoiceFeeArticleId_Kivra: this._cf.create('COMPANY.INVOICE_FEE.KIVRA_ACCOUNT', this.dataContext.invoiceFeeArticleId_Kivra),
      invoiceFeeAmount_Kivra: this._cf.create('COMMON.AMOUNT', this.dataContext.invoiceFeeAmount_Kivra),
      invoiceFeeArticleId_B2CeInvoice: this._cf.create('COMPANY.INVOICE_FEE.B2C_ACCOUNT', this.dataContext.invoiceFeeArticleId_B2CeInvoice),
      invoiceFeeAmount_B2CeInvoice: this._cf.create('COMMON.AMOUNT', this.dataContext.invoiceFeeAmount_B2CeInvoice),
      invoiceFeeArticleId_B2BeInvoice: this._cf.create('COMPANY.INVOICE_FEE.B2B_ACCOUNT', this.dataContext.invoiceFeeArticleId_B2BeInvoice),
      invoiceFeeAmount_B2BeInvoice: this._cf.create('COMMON.AMOUNT', this.dataContext.invoiceFeeAmount_B2BeInvoice),
    });

    this.AddSubscription(group.controls['nameplateOneline'].valueChanges.subscribe(x => this.updateNameplate()));
    this.AddSubscription(group.controls['nameplateShortNames'].valueChanges.subscribe(x => this.updateNameplate()));

    this.AddAccountingExportControls(group);

    this.AddSubscription(
      group.controls['ftpFilePrefix'].valueChanges.subscribe(val => {
        var prefix = val;
        if (val == undefined) prefix = ''; // fix for undefined

        var selectedValue = group.controls['ftpFileNamingConvention'].value;
        this.ftpFileNameConventionSelection = []; // clear existing

        for (let key in this.dataContext.ftpFileNamingConventionSelection) {
          this.ftpFileNameConventionSelection.push(
            new SelectionItem(
              prefix + this.dataContext.ftpFileNamingConventionSelection[key].substring(this.dataContext.ftpFilePrefix.length),
              key + ''
            )
          );
        }

        // reselect the value
        group.controls['ftpFileNamingConvention'].setValue(selectedValue);
      })
    );

    this.AddSubscription(
      group.controls['invoicingInterval'].valueChanges.subscribe(val => {
        this.offsetSelection = null;
        group.controls['imdOffset'].setValue(numberValue(val) + 1);
      })
    );

    this.AddSubscription(
      group.controls['imdFtpEnabled'].valueChanges.subscribe(val => {
        group.controls['imdFtpPassword'].setValidators(val && !this.dataContext.imdFtpEnabled ? Validators.required : null);
        this.showImdFtp = val;
      })
    );
    this.AddSubscription(
      group.controls['cultureName'].valueChanges.subscribe(val => {
        this.companyForms = CompanyForms.getCompanyFormSelection(val);
        group.controls['companyForm'].setValue(this.companyForms[0].value);
      })
    );
    return group;
  }

  updateNameplate() {
    if (this.form == null) return;
    let shortNames = this.form.controls['nameplateShortNames'].value;
    let oneLine = this.form.controls['nameplateOneline'].value;

    if (shortNames && oneLine) this.form.controls['defaultNameplate'].setValue(this.defaultNameplateOneLineShort);
    else if (shortNames && !oneLine) this.form.controls['defaultNameplate'].setValue(this.defaultNameplateShortMulti);
    else if (!shortNames && oneLine) this.form.controls['defaultNameplate'].setValue(this.defaultNameplateOneLine);
    else this.form.controls['defaultNameplate'].setValue(this.defaultNameplate);
  }

  get showImdOffset(): boolean {
    return this.form?.controls['invoicingInterval'].value > 1;
  }

  private offsetSelection: SelectionItem[];
  get imdOffsetSelection(): SelectionItem[] {
    if (this.offsetSelection) return this.offsetSelection;
    const lowestOffset = numberValue(this.form.controls['invoicingInterval'].value) + 1;
    const chargeMonth = LocalDate.fromDate(LocalDate.today().addMonths(lowestOffset - 1));
    const selection: SelectionItem[] = [];
    for (let offset = lowestOffset; offset <= lowestOffset + 9; offset++) {
      selection.push(
        new SelectionItem(this._languageService.getText(getMonthName(chargeMonth.month)) + ' ' + chargeMonth.year.toString(), offset)
      );
      chargeMonth.addMonths(1);
    }
    this.offsetSelection = selection;
    return selection;
  }

  private getImdOffsetText(company: Company): string {
    let imdMonth = LocalDate.fromDate(LocalDate.today().addMonths(-1));
    return this._languageService.getText('COMPANY.IMD_OFFSET', {
      consumptionMonth: this._languageService.getText(getMonthName(imdMonth.month)) + ' ' + imdMonth.year.toString(),
    });
  }

  get companyCostPlaces(): FormArray {
    return this.form.controls['companyCostPlaces'] as FormArray;
  }

  get companyCostBearers(): FormArray {
    return this.form.controls['companyCostBearers'] as FormArray;
  }

  private _createCompanyCostPlaceGroup(model: CompanyCostPlace): FormGroup {
    const labels = this.getCurrentDimensionLabels();
    return this._fb.group({
      id: this._cf.createHidden(model.id),
      costPlace: this._cf.createWithOptions(labels.costPlaceLabel, model.costPlace, { length: 10, required: true }),
    });
  }

  private _createCompanyCostBearerGroup(model: CompanyCostBearer): FormGroup {
    const labels = this.getCurrentDimensionLabels();
    return this._fb.group({
      id: this._cf.createHidden(model.id),
      costBearer: this._cf.createWithOptions(labels.costBearerLabel, model.costBearer, { length: 10, required: true }),
    });
  }

  addCostPlace() {
    this.companyCostPlaces.push(this._createCompanyCostPlaceGroup(new CompanyCostPlace()));
  }

  addCostBearer() {
    this.companyCostBearers.push(this._createCompanyCostBearerGroup(new CompanyCostBearer()));
  }

  get addCostPlaceButtonText(): string {
    return this._languageService.getText('ACCOUNT_TRANSACTION.ADD_DIMENSION', {
      dimension: this.getCurrentDimensionLabels().costPlaceLabel,
    });
  }

  get addCostPlaceHelpText(): string {
    return this._languageService.getText('ACCOUNT_TRANSACTION.HELP_TEXT.ADD_DIMENSION', {
      dimension: this.getCurrentDimensionLabels().costPlaceLabel,
    });
  }

  get addCostBearerButtonText(): string {
    return this._languageService.getText('ACCOUNT_TRANSACTION.ADD_DIMENSION', {
      dimension: this.getCurrentDimensionLabels().costBearerLabel,
    });
  }

  get addCostBearerHelpText(): string {
    return this._languageService.getText('ACCOUNT_TRANSACTION.HELP_TEXT.ADD_DIMENSION', {
      dimension: this.getCurrentDimensionLabels().costBearerLabel,
    });
  }

  removeCostPlace(index: number) {
    this.companyCostPlaces.removeAt(index);
    this.form.markAsDirty();
  }

  removeCostBearer(index: number) {
    this.companyCostBearers.removeAt(index);
    this.form.markAsDirty();
  }
  private ropoNovaFields = ['userName', 'password', 'client', 'division'];

  debtCollectionSupplierKinds = [
    { name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.None), value: DebtCollectionSupplierKind.None },
    {
      name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.FastighetsagarnaMittNord),
      value: DebtCollectionSupplierKind.FastighetsagarnaMittNord,
    },
    {
      name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.ObservaInkasso),
      value: DebtCollectionSupplierKind.ObservaInkasso,
    },
    { name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.Preventia), value: DebtCollectionSupplierKind.Preventia },
    { name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.Profina), value: DebtCollectionSupplierKind.Profina },
    {
      name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.PsFinanceGroup),
      value: DebtCollectionSupplierKind.PsFinanceGroup,
    },
    { name: DebtCollectionSupplierKindToString(DebtCollectionSupplierKind.Ropo), value: DebtCollectionSupplierKind.Ropo },
  ];

  createDebtCollectionForm(): FormGroup {
    let formGroupConfig = {};

    formGroupConfig['debtCollectionSupplier'] = this._cf.createWithOptions(
      'COMPANY.DEBT_COLLECTION.DEBT_COLLECTION_COMPANY',
      this.dataContext.debtCollectionConfiguration.debtCollectionSupplier,
      {
        required: false,
        helpText: 'COMPANY.HELP_TEXT.DEBT_COLLECTION.ENABLED',
      }
    );

    formGroupConfig['ropo24Enabled'] = this._cf.createHidden(this.dataContext.debtCollectionConfiguration.ropo24Enabled);

    this.createDebtCollectionFormControls(this.ropoNovaFields, formGroupConfig);
    this.debtCollectionForm = this._fb.group(formGroupConfig);
    this.showDebtCollectionRopo =
      this.dataContext.debtCollectionConfiguration.debtCollectionSupplier &&
      this.dataContext.debtCollectionConfiguration.debtCollectionSupplier === DebtCollectionSupplierKind.Ropo;

    this.AddSubscription(
      this.debtCollectionForm.controls['debtCollectionSupplier'].valueChanges.subscribe(val => {
        this.showDebtCollectionRopo = val === DebtCollectionSupplierKind.Ropo;
        this.form.markAsDirty();
      })
    );

    return this.debtCollectionForm;
  }

  private createDebtCollectionFormControls(novaFields: string[], formGroupConfig: any) {
    novaFields.forEach(field => {
      formGroupConfig[field] = this._cf.createWithOptions(
        `COMPANY.DEBT_COLLECTION.${field.toUpperCase()}`,
        this.dataContext.debtCollectionConfiguration[field],
        {
          required: false,
          helpText: `COMPANY.HELP_TEXT.DEBT_COLLECTION.${field.toUpperCase()}`,
        }
      );
    });
  }

  get isSendDebtCollectionDisabled(): boolean {
    return !this.form || this.form.dirty || !this.form.valid || false;
  }

  sendDebtCollection() {
    if (this._running) {
      return;
    }

    this._running = true;
    this.dataService
      .sendToDebtCollection(this.dataContext.id)
      .pipe(
        finalize(() => {
          this._running = false;
        })
      )
      .subscribe();
  }

  get accountPlanControl(): FormControlEx {
    return this.form.controls['accountPlanId'] as FormControlEx;
  }

  debtCollectionForm: FormGroup;
  showDebtCollectionRopo: boolean;
  paymentReceiver: string;

  protected createModel(): Company {
    return new Company();
  }

  accountPlanSelection: SelectionItem[] = [];
  private accountPlans: AccountPlan[] = [];

  eInvoiceAddress(control: AbstractControl): ValidationErrors | null {
    return /^([^:]+:[^:]+)$/.test(control.value) ? null : { eInvoiceAddress: { message: 'ERROR.FORMAT' } };
  }

  debtCollectionCaseTransferOptions: SelectionItem[] = [
    new SelectionItem('COMPANY.DBC_TRANSFER.ROPONOVA', 0, false),
    new SelectionItem('COMPANY.DBC_TRANSFER.ROPOONE', 1, false),
  ];

  debtCollectionCaseTransfer: FormControlEx;

  private debtCollectionCaseTransferChanged(value: number, isInit = false) {
    switch (value) {
      case 0:
        this.showRopo = true;
        this.showRopoOne = false;
        this.debtCollectionForm.controls['ropo24Enabled'].setValue(false);
        break;
      case 1:
        this.showRopo = false;
        this.showRopoOne = true;
        this.debtCollectionForm.controls['ropo24Enabled'].setValue(true);
        break;
    }

    if (!this.showDebtCollectionRopo) return;

    if (!isInit && this.form.pristine) this.form.markAsDirty();
  }

  protected override init(company: Company): void {
    if (this.avyIntegrationCompanyId && this.avyIntegrationCompanyId !== company?.id) {
      this.closeAvyIntegrationSidebar();
    }

    super.init(company);
    this.overviewInitialized = false;
    this.companyStatisticsLoaded = false;

    const parameters = this.navigation.getQueryParameters();
    if (parameters && (parameters.has('code') || parameters.has('authorization-code'))) {
      this.accountingConnectionInProgress = true;
    }

    this.bankAccountSelection = this.dataContext.bankAccounts
      ? this.dataContext.bankAccounts.map(
          acc => new SelectionItem(acc.bankGiroNumber ? `${acc.name} (BG ${acc.bankGiroNumber})` : acc.name, acc.id)
        )
      : [];

    this.companyForms = CompanyForms.getCompanyFormSelection(company.cultureName);
    if (this.isNew) {
      this.dataService.getNewDefaults().subscribe(
        defaults => {
          (this.form.controls['number'].setValue(defaults.number), this.form.controls['accountPlanId'].setValue(defaults.accountPlanId));
        },
        error => this.messageService.showError(error)
      );
    }
    this.AddSubscription(
      this.form.controls['logo'].valueChanges.subscribe(value => {
        if (this.form.pristine) {
          this.form.markAsDirty();
        }
      })
    );

    this.paymentReceiver = this.dataContext.paymentReceiver;
    this.AddSubscription(
      this.form.controls['name'].valueChanges.subscribe(value => {
        if (value) {
          if (!this.paymentReceiver || this.paymentReceiver === '') this.form.controls['paymentReceiver'].setValue(value);
        }
      })
    );

    this.AddSubscription(
      this.form.controls['eInvoiceSpecified'].valueChanges.subscribe(value => {
        this.eInvoice = value;
        // TODO: Add condition for .se
        if (value) {
          if (!this.form.controls['eInvoiceAddress'].value) {
            let orgNo = this.form.controls['organizationNo'].value;
            if (!orgNo) orgNo = '';
            orgNo = orgNo.replace('-', '');
            this.form.controls['eInvoiceAddress'].setValue('0007:' + orgNo);
          }
          this.form.controls['eInvoiceAddress'].setValidators([Validators.required, this.eInvoiceAddress]);
        } else this.form.controls['eInvoiceAddress'].clearValidators();
        this.form.controls['eInvoiceAddress'].updateValueAndValidity();
      })
    );

    this.AddSubscription(
      this.form.controls['isKivraIntegrationEnabled'].valueChanges.subscribe(value => {
        try {
          this.dataService
            .registerHogiaKivra(company.id)
            .pipe(
              finalize(() => {
                this._running = false;
              })
            )
            .subscribe();
        } catch (error) {}
      })
    );

    this.updateNameplate();

    if (this.dataContext.eInvoiceSpecified)
      this.form.controls['eInvoiceAddress'].setValidators([Validators.required, this.eInvoiceAddress]);

    this.eInvoice = company.eInvoiceSpecified;

    this.hasKivraLicense = company.hasKivraLicense;

    this.AddSubscription(
      this.form.controls['onlineTerminationDwellingInspection'].valueChanges.subscribe(val => this.updateDwellingInspectionType(val))
    );

    this.initializeAccountingTransfer(company);

    this.updateDwellingInspectionType(company.onlineTerminationDwellingInspection);
    this.showImdFtp = company.imdFtpEnabled;
    this.AddSubscription(
      this.articleService.getList().subscribe(data => {
        this.articles = [{ name: 'COMPANY.NO_REMINDER_FEE', value: '' }];
        this.amortizationPlanArticles = [{ name: 'NOT_SELECTED_TEXT', value: null }];
        this.invoiceFeeArticles = [{ name: 'COMPANY.INVOICE_FEE.NO_INVOICE_FEE', value: '' }];
        data.forEach(art => {
          this.articles.push({ name: art.toString(), value: art.id });
          this.amortizationPlanArticles.push({ name: art.toString(), value: art.id });
          this.invoiceFeeArticles.push({ name: art.toString(), value: art.id });
        });

        if (this.dataContext.articleId === null) {
          this.form.controls['articleId'].setValue(this.articles[0].value);
        }

        const defaultArticleId = this.form.controls['amortizationPlanDefaultArticleId'].value;
        if (defaultArticleId && !data.some(art => art.id === defaultArticleId)) {
          this.form.controls['amortizationPlanDefaultArticleId'].setValue(null);
        }
      })
    );

    this.AddSubscription(
      this.accountPlanSerive.getExtensiveList().subscribe(val => {
        this.accountPlans = val;
        this.accountPlanSelection = val.map(p => new SelectionItem(p.name, p.id));
        this.applyDimensionLabels();
      })
    );

    this.AddSubscription(this.form.controls['accountPlanId'].valueChanges.subscribe(() => this.applyDimensionLabels()));

    if (!this.isNew) {
      this.AddSubscription(
        this.dwellingInspectionTypeService.getList().subscribe(val => {
          this.dwellingInspectionTypesSelection = val.map(p => new SelectionItem(p.name, p.id));
          this.defaultInspectionType = val.find(t => t.isDefaultType);
          this.form.controls['dwellingInspectionTypeOptionalId'].setValidators(
            typesValidator(this.form.controls['dwellingInspectionTypeMainId'])
          );
          this.form.controls['dwellingInspectionTypeOptionalId'].updateValueAndValidity();
        })
      );
    }

    this.form.controls['dwellingInspectionTypeMainId'].valueChanges.subscribe(() => {
      this.form.controls['dwellingInspectionTypeOptionalId'].updateValueAndValidity();
    });

    let debtCollectionCaseType = 0;
    if (company.debtCollectionConfiguration.ropo24Enabled) {
      debtCollectionCaseType = 1;
    }
    this.debtCollectionCaseTransfer = this._cf.createHidden(debtCollectionCaseType);
    this.debtCollectionCaseTransferChanged(debtCollectionCaseType, true);
    this.AddSubscription(this.debtCollectionCaseTransfer.valueChanges.subscribe(val => this.debtCollectionCaseTransferChanged(val)));

    this.updateNameplate();
  }

  get showKivraInvoiceFee(): boolean {
    return this.form.controls['isKivraIntegrationEnabled'].value;
  }

  get showB2cInvoiceFee(): boolean {
    return this.dataContext.issuerIdentity ? true : false;
  }

  private loadCompanyStatistics(): void {
    // Fixes the issue that only one of the charts is updated.
    // TODO: check if there's any better fix.
    this.AddSubscription(
      forkJoin({
        dwellingStatistics: this.dwellingService.getCompanyStatistics(this.dataContext.id),
        otherRentalObjectStatistics: this.otherRenalObjectService.getCompanyStatistics(this.dataContext.id),
        parkingStatistics: this.parkingService.getCompanyStatistics(this.dataContext.id),
        premisesStatistics: this.premisesService.getCompanyStatistics(this.dataContext.id),
      }).subscribe({
        next: data => {
          var dwellingStatistics = data.dwellingStatistics;
          dwellingStatistics.category = RentalObjectCategory.Dwelling;
          this.dwelling = new ResidentialProperty(dwellingStatistics);
          this.dwellingExist = this.dwelling.totalRentalObjectCount > 0;

          var otherRentalObjectStatistics = data.otherRentalObjectStatistics;
          otherRentalObjectStatistics.category = RentalObjectCategory.Other;
          this.otherRentalObject = new NonResidentialProperty(otherRentalObjectStatistics);
          this.otherRentalObjectExist = this.otherRentalObject.totalRentalObjectCount > 0;

          var parkingStatistics = data.parkingStatistics;
          parkingStatistics.category = RentalObjectCategory.Parking;
          this.parking = new NonResidentialProperty(parkingStatistics);
          this.parkingExist = this.parking.totalRentalObjectCount > 0;

          var premisesStatistics = data.premisesStatistics;
          premisesStatistics.category = RentalObjectCategory.Premises;
          this.premises = new ResidentialProperty(premisesStatistics);
          this.premisesExist = this.premises.totalRentalObjectCount > 0;
          this.companyStatisticsLoaded = true;
        },
        error: error => {
          this.overviewInitialized = false;
          this.messageService.showError(error);
        },
      })
    );
  }

  onOverviewStateChanged(expanded: boolean): void {
    if (!expanded || this.overviewInitialized || !this.dataContext?.id) {
      return;
    }

    this.overviewInitialized = true;
    this.loadCompanyStatistics();
  }

  setLanguageForNameplate() {
    this.defaultNameplate = this._languageService.instant('NAMEPLATE.DEFAULT');
    this.defaultNameplateShortMulti = this._languageService.instant('NAMEPLATE.DEFAULT_SHORT');
    this.defaultNameplateOneLine = this._languageService.instant('NAMEPLATE.DEFAULT_ONELINE');
    this.defaultNameplateOneLineShort = this._languageService.instant('NAMEPLATE.DEFAULT_ONELINE_SHORT');
    this.updateNameplate();
  }
  ngOnInit() {
    super.ngOnInit();
    this.AddSubscription(
      this._userAccountService.current.subscribe(account => {
        this.canCloseAccountingPeriod = account.hasAccess(ModuleNames.Accounting, Features.CloseAccountingPeriod);
        this.canReopenAccountingPeriod = account.hasAccess(ModuleNames.Accounting, Features.ReopenAccountingPeriod);
      })
    );
    this.AddSubscription(
      this.dataContextInitialized$.subscribe(() => {
        if (!this.dataContext?.isNew) {
          this.loadSystemSettings();
        }
      })
    );
    this.AddSubscription(
      this._languageService.onLangChange.subscribe(e => {
        this._initAccountYearMonths(e.translations);
        this.setLanguageForNameplate();
      })
    );
    this.setLanguageForNameplate();
    this._initAccountYearMonths(this._languageService.getCurrentTranslations());
  }

  loadSystemSettings(): void {
    const companyId = this.dataContext?.id;
    if (!companyId) {
      return;
    }

    this.systemSettings = null;
    this.systemSettingsForm = null;

    this.AddSubscription(
      this.dataService.getSystemSettings(companyId).subscribe({
        next: settings => {
          this.systemSettings = this.normalizeSystemSettings(settings);
          this.systemSettingsForm = this.createSystemSettingsForm(this.systemSettings);
        },
        error: error => this._messageService.showError(error),
      })
    );
  }

  openSystemSettingsSidebar(section: CompanySystemSettingsSection): void {
    if (!this.systemSettings) {
      return;
    }

    this.systemSettingsForm = this.createSystemSettingsForm(this.systemSettings);
    this.systemSettingsSidebarSection = section;
    this.systemSettingsSidebarOpen = true;

    if (section === 'tenantPages') {
      this.updateTenantPageExternalUrlValidation();
      const tenantPagesForm = this.systemSettingsForm.get('tenantPages') as FormGroup;
      this.AddSubscription(tenantPagesForm.controls['enabled'].valueChanges.subscribe(() => this.updateTenantPageExternalUrlValidation()));
      this.AddSubscription(
        tenantPagesForm.controls['tenantPageSource'].valueChanges.subscribe(() => this.updateTenantPageExternalUrlValidation())
      );
    }
  }

  closeSystemSettingsSidebar(): void {
    this.systemSettingsSidebarOpen = false;
    this.systemSettingsSidebarSection = null;
  }

  openAvyIntegrationSidebar(): void {
    const companyId = this.dataContext?.id;
    if (!companyId || !this.dataContext?.avyIntegration) {
      return;
    }

    this.avyIntegrationFormSubscription?.unsubscribe();
    this.avyIntegrationForm = this.createAvyIntegrationForm(this.dataContext.avyIntegration);
    this.avyIntegrationCompanyId = companyId;
    const apiKeyControl = this.avyIntegrationForm.controls['apiKey'] as FormControlEx;
    const updateApiKeyRequirement = (enabled: boolean): void => {
      apiKeyControl.required = enabled;
    };

    updateApiKeyRequirement(this.avyIntegrationForm.controls['enabled'].value === true);
    this.avyIntegrationFormSubscription = this.avyIntegrationForm.controls['enabled'].valueChanges.subscribe(value =>
      updateApiKeyRequirement(value === true)
    );
    this.AddSubscription(this.avyIntegrationFormSubscription);
    if (this.avyIntegrationSavePending) {
      this.avyIntegrationForm.disable({ emitEvent: false });
    }
    this.avyIntegrationSidebarOpen = true;
  }

  closeAvyIntegrationSidebar(): void {
    this.avyIntegrationFormSubscription?.unsubscribe();
    this.avyIntegrationFormSubscription = null;
    this.avyIntegrationForm = null;
    this.avyIntegrationCompanyId = null;
    this.avyIntegrationSidebarOpen = false;
  }

  requestCloseAvyIntegrationSidebar(): void {
    if (!this.avyIntegrationSavePending) {
      this.closeAvyIntegrationSidebar();
    }
  }

  saveAvyIntegration(): void {
    const companyId = this.avyIntegrationCompanyId;
    const form = this.avyIntegrationForm;
    if (!companyId || companyId !== this.dataContext?.id || !form) {
      this.closeAvyIntegrationSidebar();
      return;
    }

    if (this.avyIntegrationSavePending) return;

    if (!form.valid) {
      this.validateAllFormFields(form);
      return;
    }

    const settings: CompanyAvyIntegrationSettings = {
      ...(form.value as CompanyAvyIntegrationSettings),
      useMasterDataApi: this.dataContext.avyIntegration.useMasterDataApi,
    };
    this.avyIntegrationSavePendingCompanyIds.add(companyId);
    form.disable({ emitEvent: false });
    this.AddSubscription(
      this.dataService
        .updateAvyIntegration(companyId, settings)
        .pipe(
          finalize(() => {
            this.avyIntegrationSavePendingCompanyIds.delete(companyId);
            if (this.avyIntegrationCompanyId === companyId) {
              this.avyIntegrationForm?.enable({ emitEvent: false });
            }
          })
        )
        .subscribe({
          next: () => {
            if (this.dataContext?.id !== companyId) {
              return;
            }

            this.dataContext.avyIntegration = {
              ...this.dataContext.avyIntegration,
              ...settings,
            };

            if (this.avyIntegrationForm === form) {
              this.closeAvyIntegrationSidebar();
            }
          },
          error: error => this._messageService.showError(error),
        })
    );
  }

  get avyIntegrationStatusKey(): string {
    return this.dataContext?.avyIntegration?.enabled
      ? 'COMPANY.INTEGRATION.AVY_STATUS.ACTIVATED'
      : 'COMPANY.INTEGRATION.AVY_STATUS.NOT_ACTIVATED';
  }

  get avyIntegrationEnabled(): boolean {
    return !!this.avyIntegrationForm?.controls['enabled']?.value;
  }

  get avyIntegrationSavePending(): boolean {
    return !!this.avyIntegrationCompanyId && this.avyIntegrationSavePendingCompanyIds.has(this.avyIntegrationCompanyId);
  }

  saveSystemSettings(): void {
    const companyId = this.dataContext?.id;
    if (!companyId || !this.systemSettingsForm) {
      return;
    }

    if (this.systemSettingsSidebarSection === 'tenantPages') {
      this.updateTenantPageExternalUrlValidation();
    }

    const activeForm = this.getActiveSystemSettingsForm();
    if (!activeForm.valid) {
      this.validateAllFormFields(activeForm);
      return;
    }

    const settings = this.getSystemSettingsFromForm();
    const section = this.systemSettingsSidebarSection;
    if (!section) {
      return;
    }

    this.AddSubscription(
      this.updateSystemSettingsSection(companyId, section, settings).subscribe(() => {
        this.systemSettings = settings;
        this.systemSettingsForm = this.createSystemSettingsForm(settings);
        this.closeSystemSettingsSidebar();
      })
    );
  }

  get systemSettingsSidebarTitleKey(): string {
    switch (this.systemSettingsSidebarSection) {
      case 'invoiceReminder':
        return 'COMPANY.SYSTEM_SETTINGS_BULK.TARGET.INVOICE_REMINDER';
      case 'automaticAdvicing':
        return 'COMPANY.SYSTEM_SETTINGS_BULK.TARGET.AUTOMATIC_ADVICING';
      case 'faultNotification':
        return 'COMPANY.SYSTEM_SETTINGS_BULK.TARGET.FAULT_NOTIFICATION';
      case 'tenantPages':
        return 'PROFILE_SETTINGS_FORM.TENANT_PAGES';
      case 'termination':
        return 'TERMINATE_LEASE_AGREEMENT_FORM.HEADER';
      case 'rent':
        return 'Hyra';
      case 'externalAccountsReceivable':
        return 'COMPANY.EXTERNAL_ACCOUNTS_RECEIVABLE.TITLE';
      default:
        return '';
    }
  }

  getSystemSettingsControl(path: string): AbstractControl {
    return this.systemSettingsForm.get(path) as AbstractControl;
  }

  getSelectionText(selection: SelectionItem[], value: any): string {
    return selection.find(item => item.value === value)?.name ?? '';
  }

  getTenantPageSourceText(source: TenantPageSource | null | undefined): string {
    return this.getSelectionText(this.tenantPageSourceSelection, this.normalizeTenantPageSource(source));
  }

  getEnabledText(enabled: boolean): string {
    return enabled ? 'Aktivt' : 'Avstängt';
  }

  getYesNoText(value: boolean): string {
    return value ? 'Ja' : 'Nej';
  }

  getFaultNotificationReceiversText(): string {
    return (this.systemSettings?.faultNotification?.faultNotificationReceivers || []).join('; ');
  }

  getInvoiceReminderDeliveryMethod(property: keyof CompanyInvoiceReminderSettings): ReminderDeliveryMethod {
    return this.systemSettings?.invoiceReminder?.[property] as ReminderDeliveryMethod;
  }

  getTenantPagesBoolean(property: string): boolean {
    return !!(this.systemSettings?.tenantPages as any)?.[property];
  }

  get tenantPagesSettingsEnabled(): boolean {
    return !!this.getSystemSettingsControl('tenantPages.enabled')?.value;
  }

  get tenantPagesSettingsSource(): TenantPageSource {
    return this.normalizeTenantPageSource(this.getSystemSettingsControl('tenantPages.tenantPageSource')?.value);
  }

  get showBuiltInTenantPageSettings(): boolean {
    return this.tenantPagesSettingsEnabled && this.tenantPagesSettingsSource === TenantPageSource.BuiltIn;
  }

  get showExternalTenantPageSettings(): boolean {
    return this.tenantPagesSettingsEnabled && this.tenantPagesSettingsSource === TenantPageSource.External;
  }

  get showReadonlyBuiltInTenantPageSettings(): boolean {
    return (
      !!this.systemSettings?.tenantPages?.enabled &&
      this.normalizeTenantPageSource(this.systemSettings.tenantPages.tenantPageSource) === TenantPageSource.BuiltIn
    );
  }

  get showReadonlyExternalTenantPageSettings(): boolean {
    return (
      !!this.systemSettings?.tenantPages?.enabled &&
      this.normalizeTenantPageSource(this.systemSettings.tenantPages.tenantPageSource) === TenantPageSource.External
    );
  }

  get tenantPagesOnlineTerminationEnabled(): boolean {
    return this.tenantTerminationFields.some(field => !!this.getSystemSettingsControl(`tenantPages.${field.onlineProperty}`)?.value);
  }

  get readonlyTenantPagesOnlineTerminationEnabled(): boolean {
    return this.tenantTerminationFields.some(field => this.getTenantPagesBoolean(field.onlineProperty));
  }

  private getActiveSystemSettingsForm(): FormGroup {
    if (!this.systemSettingsSidebarSection) {
      return this.systemSettingsForm;
    }

    return this.systemSettingsForm.get(this.systemSettingsSidebarSection) as FormGroup;
  }

  private normalizeSystemSettings(settings: CompanySystemSettings): CompanySystemSettings {
    return {
      ...settings,
      // Companies created before digital termination settings existed do not yet return this section.
      termination: settings.termination ?? {
        useDigitalSigningWhenTerminatedByTenant: false,
        useDigitalSigningWhenTerminatedByLandlord: false,
        requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning: false,
        landlordMessage: '',
      },
      rent: settings.rent ?? {
        roundPeriodRent: false,
      },
      externalAccountsReceivable: settings.externalAccountsReceivable,
    };
  }

  private createSystemSettingsForm(settings: CompanySystemSettings): FormGroup {
    return this._fb.group({
      invoiceReminder: this._fb.group({
        enabled: this._cf.create('Aktivt', settings.invoiceReminder.enabled),
        remindAfterDays: this._cf.create('Dagar efter förfallodatum', settings.invoiceReminder.remindAfterDays),
        reminderDeliveryMethodEmailWithPDF: this._cf.create('E-post med PDF', settings.invoiceReminder.reminderDeliveryMethodEmailWithPDF),
        reminderDeliveryMethodEmailWithoutPDF: this._cf.create(
          'E-post utan PDF',
          settings.invoiceReminder.reminderDeliveryMethodEmailWithoutPDF
        ),
        reminderDeliveryMethodMail: this._cf.create('Brev', settings.invoiceReminder.reminderDeliveryMethodMail),
        reminderDeliveryMethodManual: this._cf.create('Manuell', settings.invoiceReminder.reminderDeliveryMethodManual),
        reminderDeliveryMethodEInvoiceB2B: this._cf.create('E-faktura B2B', settings.invoiceReminder.reminderDeliveryMethodEInvoiceB2B),
        reminderDeliveryMethodEInvoiceB2C: this._cf.create('E-faktura B2C', settings.invoiceReminder.reminderDeliveryMethodEInvoiceB2C),
        reminderDeliveryMethodAvy: this._cf.create('Avy', settings.invoiceReminder.reminderDeliveryMethodAvy),
        reminderDeliveryMethodKivra: this._cf.create('Kivra', settings.invoiceReminder.reminderDeliveryMethodKivra),
        reminderDeliveryMethodNoDelivery: this._cf.create('Ingen leverans', settings.invoiceReminder.reminderDeliveryMethodNoDelivery),
        reminderDeliveryMethodSMS: this._cf.create('SMS', settings.invoiceReminder.reminderDeliveryMethodSMS),
      }),
      automaticAdvicing: this._fb.group({
        enabled: this._cf.create('Aktivt', settings.automaticAdvicing.enabled),
        automaticAdvicingDayInMonth: this._cf.createWithOptions('Dag i månaden', settings.automaticAdvicing.automaticAdvicingDayInMonth, {
          minValue: 1,
          maxValue: 31,
        }),
      }),
      faultNotification: this._fb.group({
        enabled: this._cf.create('Aktivt', settings.faultNotification.enabled),
        sendLandlordNotification: this._cf.create('Avisera hyresvärd', settings.faultNotification.sendLandlordNotification),
        faultNotificationReceivers: this._cf.create(
          'Mottagare, separerade med semikolon',
          (settings.faultNotification.faultNotificationReceivers || []).join(';')
        ),
      }),
      tenantPages: this._fb.group({
        enabled: this._cf.create('PROFILE_SETTINGS_FORM.TENANT_PAGES_LABEL', settings.tenantPages.enabled),
        tenantPageSource: this._cf.create(
          'PROFILE_SETTINGS_FORM.TENANT_PAGES_SOURCE',
          this.normalizeTenantPageSource(settings.tenantPages.tenantPageSource)
        ),
        tenantPageExternalUrl: this._cf.create(
          'PROFILE_SETTINGS_FORM.TENANT_PAGES_EXTERNAL_URL',
          settings.tenantPages.tenantPageExternalUrl ?? ''
        ),
        invoiceRegistrationMessage: this._cf.createMaxLength(
          'PROFILE_SETTINGS_FORM.TENANT_PAGES_INVOICE_INFORMATION_REGISTRATION',
          settings.tenantPages.invoiceRegistrationMessage ?? '',
          100,
          'PROFILE_SETTINGS_FORM.HELP_TEXT.TENANT_PAGES_INVOICE_INFORMATION_REGISTRATION'
        ),
        invoiceRegistrationMessageRow2: this._cf.createMaxLength(
          'PROFILE_SETTINGS_FORM.TENANT_PAGES_INVOICE_INFORMATION_REGISTRATION_ROW2',
          settings.tenantPages.invoiceRegistrationMessageRow2 ?? '',
          100,
          'PROFILE_SETTINGS_FORM.HELP_TEXT.TENANT_PAGES_INVOICE_INFORMATION_REGISTRATION'
        ),
        invoiceHasAccountMessage: this._cf.createMaxLength(
          'PROFILE_SETTINGS_FORM.TENANT_PAGES_INVOICE_HAS_ACCOUNT_INFORMATION',
          settings.tenantPages.invoiceHasAccountMessage ?? '',
          100,
          'PROFILE_SETTINGS_FORM.HELP_TEXT.TENANT_PAGES_INVOICE_HAS_REGISTRATION'
        ),
        invoiceHasAccountMessageRow2: this._cf.createMaxLength(
          'PROFILE_SETTINGS_FORM.TENANT_PAGES_INVOICE_HAS_ACCOUNT_INFORMATION_ROW2',
          settings.tenantPages.invoiceHasAccountMessageRow2 ?? '',
          100,
          'PROFILE_SETTINGS_FORM.HELP_TEXT.TENANT_PAGES_INVOICE_HAS_REGISTRATION'
        ),
        allowPrintTerminationFormDwelling: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM',
          settings.tenantPages.allowPrintTerminationFormDwelling,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_PRINT_TERMINATION_FORM_LABEL' }
        ),
        allowPrintTerminationFormOther: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM',
          settings.tenantPages.allowPrintTerminationFormOther,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_PRINT_TERMINATION_FORM_LABEL' }
        ),
        allowPrintTerminationFormParking: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM',
          settings.tenantPages.allowPrintTerminationFormParking,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_PRINT_TERMINATION_FORM_LABEL' }
        ),
        allowPrintTerminationFormPremises: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM',
          settings.tenantPages.allowPrintTerminationFormPremises,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_PRINT_TERMINATION_FORM_LABEL' }
        ),
        allowTerminationOnlineDwelling: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE',
          settings.tenantPages.allowTerminationOnlineDwelling,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_ONLINE_TERMINATION' }
        ),
        allowTerminationOnlineOther: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE',
          settings.tenantPages.allowTerminationOnlineOther,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_ONLINE_TERMINATION' }
        ),
        allowTerminationOnlineParking: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE',
          settings.tenantPages.allowTerminationOnlineParking,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_ONLINE_TERMINATION' }
        ),
        allowTerminationOnlinePremises: this._cf.createWithOptions(
          'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE',
          settings.tenantPages.allowTerminationOnlinePremises,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOW_ONLINE_TERMINATION' }
        ),
        allowedOnlineTerminationSignature: this._cf.createWithOptions(
          'PROFILE_SETTINGS_FORM.ALLOWED_ONLINE_TERMINATION_SIGNATURE',
          settings.tenantPages.allowedOnlineTerminationSignature,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.ALLOWED_ONLINE_TERMINATION_SIGNATURE' }
        ),
        displayDwellingInspection: this._cf.createWithOptions(
          'PROFILE_SETTINGS_FORM.DISPLAY_DWELLING_INSPECTION',
          settings.tenantPages.displayDwellingInspection,
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.DISPLAY_DWELLING_INSPECTION' }
        ),
        onlineTerminlationCallbackEmailAddresses: this._cf.createWithOptions(
          'PROFILE_SETTINGS_FORM.ONLINE_TERMINATION_EMAIL',
          settings.tenantPages.onlineTerminlationCallbackEmailAddresses ?? '',
          { helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.TERMINATION_EMAIL' }
        ),
      }),
      termination: this._fb.group({
        useDigitalSigningWhenTerminatedByTenant: this._cf.create(
          'COMPANY.SYSTEM_SETTINGS.TERMINATION.WHEN_TENANT_TERMINATES',
          settings.termination.useDigitalSigningWhenTerminatedByTenant
        ),
        useDigitalSigningWhenTerminatedByLandlord: this._cf.create(
          'COMPANY.SYSTEM_SETTINGS.TERMINATION.WHEN_LANDLORD_TERMINATES',
          settings.termination.useDigitalSigningWhenTerminatedByLandlord
        ),
        requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning: this._cf.create(
          'COMPANY.SYSTEM_SETTINGS.TERMINATION.WAIT_FOR_LANDLORD_TERMINATION_SIGNATURE',
          settings.termination.requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning
        ),
        landlordMessage: this._cf.createMaxLength(
          'TERMINATE_LEASE_AGREEMENT_FORM.LANDLORD_MESSAGE',
          settings.termination.landlordMessage ?? '',
          4000
        ),
      }),
      rent: this._fb.group({
        roundPeriodRent: this._cf.create('LEASE_AGREEMENT.RENTAL_CHANGES_ROUND_PERIOD_RENT', settings.rent.roundPeriodRent),
      }),
      externalAccountsReceivable: this._fb.group({
        enabled: this._cf.create('COMPANY.EXTERNAL_ACCOUNTS_RECEIVABLE.TITLE', settings.externalAccountsReceivable.enabled),
      }),
    });
  }

  private createAvyIntegrationForm(settings: Company['avyIntegration']): FormGroup {
    return this._fb.group({
      enabled: this._cf.create('COMPANY.INTEGRATION.USE_AVY', settings.enabled),
      apiKey: this._cf.create('COMPANY.INTEGRATION.AVY_API_KEY', settings.apiKey, null, null, 'COMPANY.INTEGRATION.HELP_TEXT.AVY_API_KEY'),
      sendPushNotificationsOnFaultNotificationUpdates: this._cf.create(
        'COMPANY.INTEGRATION.AVY_SEND_PUSH_NOTIFICATIONS_ON_FAULT_NOTIFICATION_UPDATES',
        settings.sendPushNotificationsOnFaultNotificationUpdates,
        null,
        null,
        'COMPANY.INTEGRATION.HELP_TEXT.AVY_SEND_PUSH_NOTIFICATIONS_ON_FAULT_NOTIFICATION_UPDATES'
      ),
    });
  }

  private getSystemSettingsFromForm(): CompanySystemSettings {
    const value = this.systemSettingsForm.value;
    return {
      invoiceReminder: value.invoiceReminder,
      automaticAdvicing: value.automaticAdvicing,
      faultNotification: {
        enabled: value.faultNotification.enabled,
        sendLandlordNotification: value.faultNotification.sendLandlordNotification,
        faultNotificationReceivers: this.splitEmailList(value.faultNotification.faultNotificationReceivers),
      },
      tenantPages: value.tenantPages,
      termination: value.termination,
      rent: value.rent,
      externalAccountsReceivable: value.externalAccountsReceivable,
    };
  }

  private updateSystemSettingsSection(
    companyId: string,
    section: CompanySystemSettingsSection,
    settings: CompanySystemSettings
  ): Observable<void> {
    switch (section) {
      case 'invoiceReminder':
        return this.dataService.updateInvoiceReminderSettings(companyId, settings.invoiceReminder as CompanyInvoiceReminderSettings);
      case 'automaticAdvicing':
        return this.dataService.updateAutomaticAdvicingSettings(companyId, settings.automaticAdvicing as CompanyAutomaticAdvicingSettings);
      case 'faultNotification':
        return this.dataService.updateFaultNotificationSettings(companyId, settings.faultNotification as CompanyFaultNotificationSettings);
      case 'tenantPages':
        return this.dataService.updateTenantPagesSettings(companyId, settings.tenantPages as CompanyTenantPagesSettings);
      case 'termination':
        return this.dataService.updateTerminationSettings(companyId, settings.termination as CompanyTerminationSettings);
      case 'rent':
        return this.dataService.updateRentSettings(companyId, settings.rent as CompanyRentSettings);
      case 'externalAccountsReceivable':
        return this.dataService.updateExternalAccountsReceivableSettings(
          companyId,
          settings.externalAccountsReceivable as CompanyExternalAccountsReceivableSettings
        );
    }
  }

  private splitEmailList(value: string): string[] {
    return (value || '')
      .split(';')
      .map(item => item.trim())
      .filter(item => !!item);
  }

  private normalizeTenantPageSource(source: TenantPageSource | null | undefined): TenantPageSource {
    return source != null && source !== TenantPageSource.None ? source : TenantPageSource.BuiltIn;
  }

  private updateTenantPageExternalUrlValidation(): void {
    if (!this.systemSettingsForm?.get('tenantPages')) return;

    const control = this.getSystemSettingsControl('tenantPages.tenantPageExternalUrl') as FormControlEx;
    const isRequired = this.tenantPagesSettingsEnabled && this.tenantPagesSettingsSource === TenantPageSource.External;

    control.required = isRequired;
    control.setValidators(isRequired ? [Validators.required, FormControlValidators.url()] : null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  onChange(files: FileList) {
    this.AddSubscription(
      this._fileService.getImage(files).subscribe(image => {
        this.form.controls['logo'].setValue(image);
      })
    );
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControlEx) {
        control.markAsTouched();
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  removeLogo() {
    if (this.dataContext?.logoSrc === FailedToLoadLogoSrc) {
      this.dataContext.logoSrc = null;
    }
    if (this.uploadFileElement.nativeElement instanceof HTMLInputElement) {
      this.uploadFileElement.nativeElement.value = null;
      this.form.controls['logo'].setValue(null);
    }
  }

  onSubmit() {
    super.onSubmit();
  }

  uploadLogo() {
    if (this.dataContext?.logoSrc === FailedToLoadLogoSrc) {
      this.dataContext.logoSrc = null;
    }
    if (this.uploadFileElement.nativeElement instanceof HTMLInputElement) {
      this.uploadFileElement.nativeElement.click();
    }
  }

  bankAccountSelection: SelectionItem[] = [];
  private _updateBankAccountSelection(values: any[]) {
    this.bankAccountSelection = values.map(
      acc => new SelectionItem(acc.bankGiroNumber ? `${acc.name} (BG ${acc.bankGiroNumber})` : acc.name, acc.id)
    );
  }

  onBankAccountsChanged(accounts: CompanyBankAccount[]): void {
    this.dataContext.bankAccounts = accounts;
    this._updateBankAccountSelection(accounts);
  }

  openAccountPlanSidebar(): void {
    this.accountPlanSidebarOpen = true;
  }

  closeAccountPlanSidebar(): void {
    this.accountPlanSidebarOpen = false;
  }

  onAccountPlanChanged(accountPlan: AccountPlan): void {
    this.dataContext.accountPlanId = accountPlan.id;
    this.dataContext.useCostPlace = accountPlan.enableCostPlace;
    this.dataContext.useCostBearer = accountPlan.enableCostBearer;
    this.dataContext.useProject = accountPlan.enableProject;
    this.dataContext.useCounterpart = accountPlan.enableCounterPart;
    this.form.controls['accountPlanId'].setValue(accountPlan.id);
    this.accountPlanSidebarOpen = false;
    this.applyDimensionLabels();
    this.bankAccountsComponent?.loadAccounts();
  }

  closeAccountingPeriod(): void {
    this.openAccountingPeriodDialog('close');
  }

  reopenAccountingPeriod(): void {
    this.openAccountingPeriodDialog('reopen');
  }

  private openAccountingPeriodDialog(mode: 'close' | 'reopen'): void {
    if (!this.dataContext?.id || this._running) return;

    this.AddSubscription(
      this._actionService
        .open(SetLockDateComponent, this._injector, null, { company: this.dataContext, mode })
        .subscribe((run: OpeningBalanceRun) => {
          if (!run) return;

          if (mode === 'close') {
            this.dataContext.accountingClosedTo = run.closedToDate;
          }
        })
    );
  }

  protected prepareSave(): Company {
    this.resetInspectionTypesOnSettingDisable();

    const imdFtpPasswordControl = this.form.controls['imdFtpPassword'];
    let model = super.prepareSave();
    if (imdFtpPasswordControl.pristine && model.imdFtpPassword === IMD_FTP_PASSWORD_PLACEHOLDER) {
      model.imdFtpPassword = '';
    }

    if (this.dataContext?.logoSrc === FailedToLoadLogoSrc) {
      model.logoSrc = FailedToLoadLogoSrc; // this is not included in Company.toJSON() so it doesn't cause any issue.
    }

    model.accountingTransferIntegrationType = this.accountingTransferIntegrationType;
    model.ftpFileFormat = this.form.controls['ftpFileFormat'].value;
    model.address.id = this.dataContext.address.id;

    model.amortizationPlanIntegration = {
      enabled: this.form.controls['amortizationPlanEnabled'].value,
      letterText: this.form.controls['amortizationPlanLetterText'].value,
      defaultArticleId: this.form.controls['amortizationPlanDefaultArticleId'].value || null,
    };

    model.emailAccountingExportConfiguration.enabled = this.accountingTransferIntegrationType == AccountingExportMethodKind.SIE4Email;

    model.dinBoxIntegration = {
      enabled: this.form.controls['dinBoxEnabled'].value,
      userName: this.form.controls['dinBoxUserName'].value,
      password: this.form.controls['dinBoxPassword'].value,
    };

    var short = this.form.controls['nameplateShortNames'].value;
    var oneline = this.form.controls['nameplateOneline'].value;
    var enabled = this.form.controls['nameplateEnabled'].value;

    model.nameplateType = CompanyNameplateType.Auto;
    if (enabled) {
      if (short) {
        model.nameplateType = oneline ? CompanyNameplateType.OnelineShort : CompanyNameplateType.MultilineShort;
      } else {
        model.nameplateType = oneline ? CompanyNameplateType.Oneline : CompanyNameplateType.Auto;
      }
    }

    model.vismaControlEdgeIntegration = {
      enabled: this.accountingTransferIntegrationType == AccountingExportMethodKind.VismaControlEdge,
      storageSpaceId: this.form.controls['vismaControlEdgeStorageSpaceId'].value,
      tenantId: this.form.controls['vismaControlEdgeTenantId'].value,
      businessUnitName: this.form.controls['vismaControlEdgeBusinessUnitName'].value,
      o1: this.form.controls['vismaControlEdgeO1'].value,
      o2: this.form.controls['vismaControlEdgeO2'].value,
      o3: this.form.controls['vismaControlEdgeO3'].value,
    };

    model.kivraIntegration = { enabled: this.form.controls['isKivraIntegrationEnabled'].value };

    model.hogiaObConfiguration = {
      bundleVouchers: this.form.controls['hogiaObBundleVouchers'].value,
      asymmetricBundling: this.form.controls['hogiaObAsymmetricBundling'].value,
    };

    if (Object.keys((this.form.controls['gl07ExportConfiguration'] as FormGroup).controls).length === 0)
      model.gl07ExportConfiguration = null;
    if (Object.keys((this.form.controls['sie4ExportConfiguration'] as FormGroup).controls).length === 0)
      model.sie4ExportConfiguration = null;
    if (Object.keys((this.form.controls['raindanceExportConfiguration'] as FormGroup).controls).length === 0)
      model.raindanceExportConfiguration = null;
    else model.raindanceExportConfiguration.enabled = this.accountingTransferIntegrationType == AccountingExportMethodKind.RaindanceFtp;
    if (Object.keys((this.form.controls['vismaRorExportConfiguration'] as FormGroup).controls).length === 0)
      model.vismaRorExportConfiguration = null;
    else model.vismaRorExportConfiguration.enabled = this.accountingTransferIntegrationType == AccountingExportMethodKind.VismaRorFtp;

    return model;
  }

  protected override formSubmittedChange(response?: any): void {
    super.formSubmittedChange(response);

    const imdFtpEnabled = !!this.form.controls['imdFtpEnabled'].value;
    this.dataContext.imdFtpEnabled = imdFtpEnabled;
    this.form.controls['imdFtpPassword'].setValue(imdFtpEnabled ? IMD_FTP_PASSWORD_PLACEHOLDER : '', { emitEvent: false });
  }

  private resetInspectionTypesOnSettingDisable() {
    if (!this.form.controls['onlineTerminationDwellingInspection'].value && this.form.controls['dwellingInspectionTypeMainId'].value) {
      this.form.controls['dwellingInspectionTypeMainId'].setValue(null);
      this.form.controls['dwellingInspectionTypeOptionalId'].setValue(null);
    }
  }

  //
  // Everything below related to custom property
  //
  properties: {
    form: FormGroup;
    selection: SelectionItem[];
    showInt: boolean;
    showText: boolean;
    showDate: boolean;
    showBoolean: boolean;
    showSelection: boolean;
    showNumeric: boolean;
  }[];

  private _createPropertiesForm(companyCustomProperties: CompanyCustomProperty[]): FormArray {
    const formPropertiesArray = this._fb.array([]);
    this.properties = [];
    for (let companyProperty of companyCustomProperties) {
      let form = this._fb.group({
        id: this._cf.createHidden(companyProperty.id),
        customPropertyId: this._cf.createHidden(companyProperty.customPropertyId),
        textValue: this._cf.create(companyProperty.caption, companyProperty.textValue),
        numericValue: this._cf.create(companyProperty.caption, companyProperty.numericValue),
        boolValue: this._cf.create(companyProperty.caption, companyProperty.boolValue),
        dateValue: this._cf.create(companyProperty.caption, companyProperty.dateValue),
        dataType: this._cf.create(companyProperty.caption, companyProperty.dataType),
      });
      formPropertiesArray.push(form);

      this.properties.push({
        form: form,
        selection: companyProperty.selectableValues.map(x => new SelectionItem(x, x)),
        showInt: companyProperty.dataType === CustomPropertyDataType.Numeric && companyProperty.decimalCount === 0,
        showText: companyProperty.dataType === CustomPropertyDataType.Text,
        showDate: companyProperty.dataType === CustomPropertyDataType.Date,
        showBoolean: companyProperty.dataType === CustomPropertyDataType.Boolean,
        showSelection: companyProperty.dataType === CustomPropertyDataType.Selection,
        showNumeric: companyProperty.dataType === CustomPropertyDataType.Numeric && companyProperty.decimalCount !== 0,
      });
    }

    return formPropertiesArray;
  }

  private updateDwellingInspectionType(enable: boolean): void {
    let control = this.form.controls['dwellingInspectionTypeMainId'];
    if (enable) {
      control.setValidators(Validators.required);
      control.value ? control.markAsTouched() : control.setValue(this.defaultInspectionType?.id);
    } else {
      control.setValidators(null);
    }
    control.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._accFormSub?.unsubscribe();
  }

  // Everything below this line is related to accouting transfer
  hogiaEnableAccountingText: string;
  hogiaAccountingText: string;

  vismaEnableAccountingText: string;
  vismaConnectButtonText: string;
  vismaAccountingText: string;

  get vismaOnlineSelected(): boolean {
    return this.accountingTransferIntegrationType === AccountingExportMethodKind.Visma;
  }

  get manualAccountingTransfer(): boolean {
    if (this.manuallySelectedTab && this.manuallySelectedTab !== 'manual') return false;

    return this.accountingTransferIntegrationType === AccountingExportMethodKind.None;
  }

  get vismaEnabledOnLoad(): boolean {
    return (
      this.dataContext.accountingTransferIntegrationType === AccountingExportMethodKind.Visma ||
      this.dataContext.accountingTransferIntegrationType === AccountingExportMethodKind.VismaControlEdge
    );
  }

  get fortnoxEnabledOnLoad(): boolean {
    return this.dataContext.accountingTransferIntegrationType === AccountingExportMethodKind.Fortnox;
  }

  get hogiaEnabledOnLoad(): boolean {
    return (
      this.dataContext.accountingTransferIntegrationType === AccountingExportMethodKind.HogiaHek ||
      this.dataContext.accountingTransferIntegrationType === AccountingExportMethodKind.HogiaOb
    );
  }

  get ftpEnabled(): boolean {
    return (
      this.accountingTransferIntegrationType === AccountingExportMethodKind.SIE4Ftp ||
      this.accountingTransferIntegrationType === AccountingExportMethodKind.AgressoGl07Ftp ||
      this.accountingTransferIntegrationType === AccountingExportMethodKind.RaindanceFtp ||
      this.accountingTransferIntegrationType === AccountingExportMethodKind.VismaRorFtp
    );
  }

  get emailTransferEnabled(): boolean {
    return this.accountingTransferIntegrationType === AccountingExportMethodKind.SIE4Email;
  }

  get showAccountingTransferOptions(): boolean {
    return !this.vismaEnabledOnLoad && !this.fortnoxEnabledOnLoad;
  }

  get accountingTransferIntegrationType(): AccountingExportMethodKind {
    return this.form.controls['accountingTransferIntegrationType'].value;
  }

  set accountingTransferIntegrationType(value: AccountingExportMethodKind) {
    this.form.controls['accountingTransferIntegrationType'].setValue(value);
  }

  private setAccountingTransferIntegrationType(value: AccountingExportMethodKind): void {
    this.accountingTransferIntegrationType = value;
    this.dataContext.accountingTransferIntegrationType = value;
    this.form.controls['accountingTransferIntegrationType'].markAsDirty();
  }

  private getAccountingTransferIntegrationTypeForFtpFileFormat(): AccountingExportMethodKind {
    switch (this.form.controls['ftpFileFormat'].value) {
      case AccountingFileFormat.Gl07:
        return AccountingExportMethodKind.AgressoGl07Ftp;
      case AccountingFileFormat.Raindance:
        return AccountingExportMethodKind.RaindanceFtp;
      case AccountingFileFormat.VismaRor:
        return AccountingExportMethodKind.VismaRorFtp;
      default:
        return AccountingExportMethodKind.SIE4Ftp;
    }
  }

  public connectFortnox() {
    const proceed$ =
      this.form?.dirty || this.innerFormDirty() ? this._messageService.showDialog('PENDING_CHANGES_TEXT_ACTION') : of(true);

    this.AddSubscription(
      proceed$
        .pipe(
          take(1),
          filter(proceed => proceed),
          switchMap(() => this.dataService.getFortnoxRegisterUrl(this.dataContext.id))
        )
        .subscribe({
          next: url => this._nativeWindow.navigate(this._nativeWindow.window, url),
          error: error => this._messageService.showError(error),
        })
    );
  }

  public connectVisma() {
    let isVismaOnline = this.form.controls['accountingChoiceVisma'].value === 'vismaonline';
    if (isVismaOnline) {
      this.execute(new Command(companyCommand.vismaConfig));
    } else {
      this._accFormSub?.unsubscribe();
      this._accFormSub = this._messageService.showDialog('VISMA_CONTROL_EDGE.BEFORE_CONNECT_INFO').subscribe(res => {
        if (!res) return;
        this.accountingTransferIntegrationType = AccountingExportMethodKind.VismaControlEdge;
        this.dataContext.accountingTransferIntegrationType = AccountingExportMethodKind.VismaControlEdge;
        this.form.controls['accountingTransferIntegrationType'].markAsDirty();
      });
    }
  }

  public connectHogia() {
    this._accFormSub?.unsubscribe();
    let isHek = this.form.controls['accountingChoiceHogia'].value === 'hek';
    if (isHek) {
      this._accFormSub = this._messageService.showDialog('HOGIAHEK.CONNECT_REDIRECT_INFO').subscribe(res => {
        if (!res) return;
        this.accountingTransferIntegrationType = AccountingExportMethodKind.HogiaHek;
        this.dataContext.accountingTransferIntegrationType = AccountingExportMethodKind.HogiaHek;
        this.form.controls['accountingTransferIntegrationType'].markAsDirty();
        this.updateHogia(AccountingExportMethodKind.HogiaHek);
      });
    } else {
      this._accFormSub = this._actionService.open(CompanyHogiaConfigComponent, this._injector, null, this.dataContext).subscribe(res => {
        if (res) {
          this.accountingTransferIntegrationType = AccountingExportMethodKind.HogiaOb;
          this.dataContext.accountingTransferIntegrationType = AccountingExportMethodKind.HogiaOb;
          this.form.controls['accountingTransferIntegrationType'].markAsDirty();
          this.updateHogia(AccountingExportMethodKind.HogiaOb);
        }
      });
    }
  }

  updateVismaControlEdge(enable: boolean): void {
    this.form.controls['vismaControlEdgeTenantId'].setValidators(enable ? Validators.required : null);
    this.form.controls['vismaControlEdgeTenantId'].markAllAsTouched();
    this.form.controls['vismaControlEdgeStorageSpaceId'].setValidators(enable ? Validators.required : null);
    this.form.controls['vismaControlEdgeStorageSpaceId'].markAllAsTouched();
    this.updateVismaAccountingText();
  }

  updateHogia(selectedIntegration: AccountingExportMethodKind): void {
    this.form.controls['hogiaOpenBusinessApiKey'].setValidators(
      selectedIntegration === AccountingExportMethodKind.HogiaHek || selectedIntegration === AccountingExportMethodKind.HogiaOb
        ? Validators.required
        : null
    );
    this.form.controls['hogiaOpenBusinessApiKey'].markAllAsTouched();
    this.updateHogiaAccountingText();
  }

  selectedAccountingTabId = 'manual';
  private _accFormSub: Subscription;
  public disconnectAccounting() {
    this._accFormSub?.unsubscribe();
    this._accFormSub = this._messageService
      .showDialog('COMPANY.DISCONNECT_ACCOUNTING_WARNING')
      .pipe(
        switchMap(res => {
          if (!res) return of(false);
          return this.dataService.disableAccountingIntegration(this.dataContext.id).pipe(switchMap(r => of(res)));
        })
      )
      .subscribe(res => {
        if (!res) return;
        this.dataContext.accountingTransferIntegrationType = AccountingExportMethodKind.None;
        this.accountingTransferIntegrationType = AccountingExportMethodKind.None;
        this.selectedAccountingTabId = 'manual';
        this.manuallySelectedTab = 'manual';
      });
  }

  get isConnectToHogiaObDisabled(): boolean {
    const verificationNumberSeriesGeneral: string = this.form.controls['verificationNumberSeriesGeneral'].value;
    if (verificationNumberSeriesGeneral && verificationNumberSeriesGeneral.length === 1) {
      const pattern = /^[A-Sa-s]$/; // Regular expression pattern

      // Use the test method of the RegExp object to match the string against the pattern
      return !pattern.test(verificationNumberSeriesGeneral);
    }

    return true;
  }

  ftpFormFields = ['ftpProtocol', 'ftpAddress', 'ftpPort', 'ftpUser', 'ftpPassword', 'ftpPath', 'ftpTimeOfDay'];

  testFtp() {
    let formValues = this.form.value;
    if (this.ftpFormFields.every(field => this.form.controls[field].valid)) {
      this.dataService
        .testFtp(
          formValues.ftpProtocol,
          formValues.ftpAddress,
          formValues.ftpPort,
          formValues.ftpUser,
          formValues.ftpPassword,
          formValues.ftpPath
        )
        .pipe(
          catchError(err => {
            this.messageService.showError(err);
            throw err;
          })
        )
        .subscribe();
    }
  }

  downloadGl07() {
    if (this._running) return;
    this._running = true;
    this.AddSubscription(
      this.dataService
        .downloadGl07(
          this.dataContext.id,
          this.form.controls['ftpFilePrefix'].value,
          (this.form.controls['gl07ExportConfiguration'] as FormGroup).getRawValue() as Gl07ExportConfiguration
        )
        .pipe(
          finalize(() => {
            this._running = false;
          })
        )
        .subscribe(res => {
          this._nativeWindow.saveWithDecodedFileName(res);
        })
    );
  }

  downloadRaindance() {
    if (this._running) return;
    this._running = true;
    this.AddSubscription(
      this.dataService
        .downloadRaindance(
          this.dataContext.id,
          this.form.controls['ftpFilePrefix'].value,
          (this.form.controls['raindanceExportConfiguration'] as FormGroup).getRawValue() as RaindanceExportConfiguration
        )
        .pipe(
          finalize(() => {
            this._running = false;
          })
        )
        .subscribe(res => {
          this._nativeWindow.saveWithDecodedFileName(res);
        })
    );
  }

  downloadVismaRor() {
    if (this._running) return;
    this._running = true;
    this.AddSubscription(
      this.dataService
        .downloadVismaRor(
          this.dataContext.id,
          this.form.controls['ftpFilePrefix'].value,
          (this.form.controls['vismaRorExportConfiguration'] as FormGroup).getRawValue() as VismaRorExportConfiguration
        )
        .pipe(
          finalize(() => {
            this._running = false;
          })
        )
        .subscribe(res => {
          this._nativeWindow.saveWithDecodedFileName(res);
        })
    );
  }

  private _running: boolean = false;
  sendAccountingToProvider() {
    if (this._running) {
      return;
    }
    if (!this.form.pristine) {
      this._messageService.showWarning('COMPANY.ACCOUNTING_TRANSFER_TRANSFER_WARNING_NOT_SAVED');
      return;
    }
    this._running = true;
    this.dataService
      .sendToProvider(this.dataContext.id)
      .pipe(
        finalize(() => {
          this._running = false;
        })
      )
      .subscribe();
  }

  accountingToSelection: SelectionItem[] = [
    new SelectionItem('COMPANY.ACCOUNTING_EXPORT_DATE_LIMIT.ALL', AccountingExportDateLimit.NoLimit),
    new SelectionItem('COMPANY.ACCOUNTING_EXPORT_DATE_LIMIT.LAST_MONTH', AccountingExportDateLimit.LastMonthBreak),
    new SelectionItem('COMPANY.ACCOUNTING_EXPORT_DATE_LIMIT.TODAY', AccountingExportDateLimit.Today),
  ];

  ftpFileNameConventionSelection: SelectionItem[] = [];
  dimensionSelection: SelectionItem[] = createDimensionSelection();
  gl07DimensionSelection: SelectionItem[] = createDimensionSelection(undefined, true);
  customerOrSupplierIdDimSelection: SelectionItem[] = [];
  sendAccountingDaySelection: SelectionItem[];
  ftpFileFormats: SelectionItem[] = [
    new SelectionItem('COMPANY.FTP_FILE_FORMAT_SIE4', AccountingFileFormat.Sie4),
    new SelectionItem('COMPANY.FTP_FILE_FORMAT_GL07', AccountingFileFormat.Gl07),
    new SelectionItem('COMPANY.FTP_FILE_FORMAT_RAINDANCE', AccountingFileFormat.Raindance),
    new SelectionItem('COMPANY.FTP_FILE_FORMAT_VISMA_ROR', AccountingFileFormat.VismaRor),
  ];

  readonly accountingTabKeyHogia = 'hogia';
  readonly accountingTabKeySftp = 'sftp';
  readonly accountingTabKeyEmail = 'email';

  get accountingTabs(): readonly AstralTab[] {
    const tabs: AstralTab[] = [
      { id: 'manual', labelKey: 'COMPANY.ACC_TRANSFER.MANUAL' },
      { id: this.accountingTabKeyHogia, labelKey: 'COMPANY.ACC_TRANSFER.HOGIA' },
      { id: 'fortnox', labelKey: 'COMPANY.ACC_TRANSFER.FORTNOX' },
      { id: 'visma', labelKey: 'COMPANY.ACC_TRANSFER.VISMA' },
      { id: this.accountingTabKeySftp, labelKey: 'COMPANY.ACC_TRANSFER.SFTP' },
    ];

    if (this.emailTransferForm) {
      tabs.push({ id: this.accountingTabKeyEmail, labelKey: 'COMPANY.ACC_TRANSFER.EMAIL' });
    }

    return tabs;
  }

  private setAccountingSelectedTab() {
    if (this.hogiaEnabledOnLoad) {
      this.selectedAccountingTabId = this.accountingTabKeyHogia;
    } else if (this.ftpEnabled) {
      this.selectedAccountingTabId = this.accountingTabKeySftp;
    } else if (this.emailTransferEnabled) {
      this.selectedAccountingTabId = this.accountingTabKeyEmail;
    } else {
      this.selectedAccountingTabId = 'manual';
    }
  }

  manuallySelectedTab?: string;
  onAccountingTabChange(tabId: string) {
    this.selectedAccountingTabId = tabId;
    this.manuallySelectedTab = tabId;

    if (tabId === 'manual') {
      this.setAccountingTransferIntegrationType(AccountingExportMethodKind.None);
      this.form.controls['firstAccountingDateToExport'].setValidators(null);
      this.form.controls['firstAccountingDateToExport'].updateValueAndValidity();
    }

    if (tabId === this.accountingTabKeySftp) {
      this.setAccountingTransferIntegrationType(this.getAccountingTransferIntegrationTypeForFtpFileFormat());
    }

    if (tabId === this.accountingTabKeyEmail) {
      this.setAccountingTransferIntegrationType(AccountingExportMethodKind.SIE4Email);
    }

    if (tabId !== 'manual') {
      this.form.controls['firstAccountingDateToExport'].setValidators(Validators.required);
      this.form.controls['firstAccountingDateToExport'].updateValueAndValidity();
      setTimeout(() => this.form.controls['firstAccountingDateToExport'].markAsTouched(), 1);
    }
  }

  accountingConnectionInProgress: boolean = false;

  private updateHogiaAccountingText(): void {
    let isHek = this.form.controls['accountingChoiceHogia'].value === 'hek';
    let toTranslate: string = isHek ? 'HOGIAHEK.CONNECT_INFO' : 'HOGIA.CONNECT_INFO';
    this.hogiaEnableAccountingText = getTranslation(this._languageService.getCurrentTranslations(), toTranslate);
    toTranslate = isHek ? 'HOGIAHEK.CONNECTED_INFO' : 'HOGIA.CONNECTED_INFO';
    this.hogiaAccountingText = getTranslation(this._languageService.getCurrentTranslations(), toTranslate);
  }

  private updateVismaAccountingText(): void {
    let isVismaOnline = this.form.controls['accountingChoiceVisma'].value === 'vismaonline';
    let toTranslate: string = isVismaOnline ? 'VISMA.CONNECT_INFO' : 'VISMA_CONTROL_EDGE.CONNECT_INFO';
    this.vismaEnableAccountingText = getTranslation(this._languageService.getCurrentTranslations(), toTranslate);
    toTranslate = isVismaOnline ? 'VISMA.CONNECTED_INFO' : 'VISMA_CONTROL_EDGE.CONNECTED_INFO';
    this.vismaAccountingText = getTranslation(this._languageService.getCurrentTranslations(), toTranslate);
    toTranslate = isVismaOnline ? 'VISMA.CONNECT' : 'VISMA_CONTROL_EDGE.CONNECT';
    this.vismaConnectButtonText = getTranslation(this._languageService.getCurrentTranslations(), toTranslate);
  }

  updateEmailTransfer(enable: boolean): void {
    let form = this.form.controls['emailAccountingExportConfiguration'] as FormGroup;
    for (let field of ['emailAddress']) {
      let control = form.controls[field];
      if (enable) {
        control.setValidators(Validators.required);
        control.markAsTouched();
      } else {
        control.setValidators(null);
      }
      control.updateValueAndValidity();
    }
    this.form.updateValueAndValidity();
  }

  updateFtp(enable: boolean): void {
    for (let field of this.ftpFormFields) {
      let control = this.form.controls[field];
      if (enable) {
        control.setValidators(Validators.required);
        control.markAsTouched();
      } else {
        control.setValidators(null);
      }
      control.updateValueAndValidity();
    }
    this.form.updateValueAndValidity();
  }

  ftpProtocols: SelectionItem[] = [new SelectionItem('FTP', 'FTP'), new SelectionItem('SFTP', 'SFTP')];

  accountingTransferTimeIntervals: SelectionItem[] = [
    new SelectionItem('00:00-04:00', 0),
    new SelectionItem('04:00-08:00', 4),
    new SelectionItem('08:00-12:00', 8),
    new SelectionItem('12:00-16:00', 12),
    new SelectionItem('16:00-20:00', 16),
    new SelectionItem('20:00-24:00', 20),
  ];

  accountingChoicesHogia = [
    {
      value: 'ob',
      displayName: 'Hogia OpenBusiness',
    },
    {
      value: 'hek',
      displayName: 'Hogia Ekonomi',
    },
  ];

  accountingChoicesVisma = [
    {
      value: 'vismaonline',
      displayName: 'Spiris',
    },
    {
      value: 'vismacontroledge',
      displayName: 'Visma Control Edge',
    },
  ];

  emailTransferForm: FormGroup;

  private AddAccountingExportControls(form: FormGroup): void {
    form.addControl('accountingTransferIntegrationType', this._cf.createHidden(this.dataContext.accountingTransferIntegrationType));
    form.addControl(
      'accountingExportDateLimit',
      this._cf.create('COMPANY.ACCOUNTING_EXPORT_DATE_LIMIT.FIELD_NAME', this.dataContext.accountingExportDateLimit)
    );
    form.addControl(
      'accountingExportErrorEmailAddress',
      this._cf.create(
        'COMPANY.ACCOUNTING_EXPORT_ERROR_EMAIL_ADDRESS',
        this.dataContext.accountingExportErrorEmailAddress,
        // Validators.Email only works with single email. This pattern works for multiple email with , or ; as seperators
        Validators.pattern(
          /^([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~\-]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+)([,;] ?[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~\-]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+)*$/
        ),
        null,
        'COMPANY.HELP_TEXT.ACCOUNTING_EXPORT_ERROR_EMAIL_ADDRESS'
      )
    );

    form.addControl(
      'firstAccountingDateToExport',
      this._cf.create(
        'COMPANY.ACCOUNTING_TRANSFER_FROM',
        this.dataContext.firstAccountingDateToExport,
        this.dataContext.accountingTransferIntegrationType === AccountingExportMethodKind.None ? null : Validators.required,
        null,
        'COMPANY.HELP_TEXT.ACCOUNTING_TRANSFER_FROM'
      )
    );

    this.emailTransferForm = this._fb.group({
      emailAddress: this._cf.create('COMPANY.EMAIL', this.dataContext.emailAccountingExportConfiguration.emailAddress),
      dayOfMonth: this._cf.create(
        'COMPANY.ACCOUNTING_EMAIL_DAYOFMONTH',
        this.dataContext.emailAccountingExportConfiguration.dayOfMonth,
        null,
        null,
        'COMPANY.HELP_TEXT.ACCOUNTING_EMAIL_DAYOFMONTH'
      ),
    });

    form.addControl(
      'hogiaOpenBusinessApiKey',
      this._cf.create(
        'HOGIA.APIKEY',
        this.dataContext.hogiaOpenBusinessApiKey,
        this.hogiaEnabledOnLoad ? Validators.required : null,
        null,
        'HOGIA.HELP_TEXT.APIKEY'
      )
    );

    form.addControl(
      'hogiaObBundleVouchers',
      this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.BUNDLE_FILE', this.dataContext.hogiaObConfiguration?.bundleVouchers ?? false)
    );
    form.addControl(
      'hogiaObAsymmetricBundling',
      this._cf.create(
        'COMPANY.SETTINGS_GL07_FIELDS.ASYMMETRIC_BUNDLING',
        this.dataContext.hogiaObConfiguration?.asymmetricBundling ?? false
      )
    );

    form.addControl('ftpProtocol', this._cf.create('COMPANY.FTP_PROTOCOL', this.dataContext.ftpProtocol));
    form.addControl('ftpAddress', this._cf.create('COMPANY.FTP_ADDRESS', this.dataContext.ftpAddress));
    form.addControl('ftpPort', this._cf.create('COMPANY.FTP_PORT', this.dataContext.ftpPort));
    form.addControl('ftpUser', this._cf.create('USER.NAME', this.dataContext.ftpUser));
    form.addControl('ftpPassword', this._cf.create('USER.PASSWORD', this.dataContext.ftpPassword));
    form.addControl('ftpPath', this._cf.create('COMPANY.FTP_PATH', this.dataContext.ftpPath ? this.dataContext.ftpPath : '.'));
    form.addControl(
      'ftpTimeOfDay',
      this._cf.create(
        'COMPANY.ACC_TRANSFER_TIME_OF_DAY',
        this.dataContext.ftpTimeOfDay ? this.dataContext.ftpTimeOfDay : 0,
        null,
        null,
        'COMPANY.HELP_TEXT.ACC_TRANSFER_TIME_OF_DAY'
      )
    );
    form.addControl(
      'ftpFileNamingConvention',
      this._cf.create(
        'COMPANY.FTP_FILENAME_CONVENTION',
        this.dataContext.ftpFileNamingConvention ? this.dataContext.ftpFileNamingConvention + '' : '0'
      )
    );
    form.addControl(
      'ftpFileFormat',
      this._cf.create('COMPANY.FTP_FILE_FORMAT', this.dataContext.ftpFileFormat, null, null, 'COMPANY.HELP_TEXT.FTP_FILE_FORMAT')
    );
    form.addControl('sie4ExportConfiguration', this._fb.group({}));
    form.addControl('gl07ExportConfiguration', this._fb.group({}));
    form.addControl('raindanceExportConfiguration', this._fb.group({}));
    form.addControl('vismaRorExportConfiguration', this._fb.group({}));
    form.addControl('emailAccountingExportConfiguration', this.emailTransferForm);

    form.addControl(
      'accountingChoiceHogia',
      this._cf.createHidden(this.dataContext.accountingTransferIntegrationType == AccountingExportMethodKind.HogiaHek ? 'hek' : 'ob')
    );
    form.addControl(
      'accountingChoiceVisma',
      this._cf.createHidden(
        this.dataContext.accountingTransferIntegrationType == AccountingExportMethodKind.Visma ? 'vismaonline' : 'vismacontroledge'
      )
    );
    form.addControl(
      'vismaControlEdgeTenantId',
      this._cf.create('VISMA_CONTROL_EDGE.TENANT_ID', this.dataContext.vismaControlEdgeIntegration?.tenantId)
    );
    form.addControl(
      'vismaControlEdgeStorageSpaceId',
      this._cf.create('VISMA_CONTROL_EDGE.STORAGE_SPACE_ID', this.dataContext.vismaControlEdgeIntegration?.storageSpaceId)
    );
    form.addControl(
      'vismaControlEdgeBusinessUnitName',
      this._cf.create(
        'VISMA_CONTROL_EDGE.BUSINESS_UNIT_NAME',
        this.dataContext.vismaControlEdgeIntegration?.businessUnitName,
        null,
        null,
        'VISMA_CONTROL_EDGE.HELP_TEXT.BUSINESS_UNIT_NAME'
      )
    );
    form.addControl(
      'vismaControlEdgeO1',
      this._cf.create('ACCOUNTING_EXPORT.DIMENSION_1', this.dataContext.vismaControlEdgeIntegration?.o1)
    );
    form.addControl(
      'vismaControlEdgeO2',
      this._cf.create('ACCOUNTING_EXPORT.DIMENSION_2', this.dataContext.vismaControlEdgeIntegration?.o2)
    );
    form.addControl(
      'vismaControlEdgeO3',
      this._cf.create('ACCOUNTING_EXPORT.DIMENSION_3', this.dataContext.vismaControlEdgeIntegration?.o3)
    );
  }

  initializeAccountingTransfer(company: Company): void {
    this.setAccountingSelectedTab();

    this.AddSubscription(
      this.form.controls['accountingTransferIntegrationType'].valueChanges.subscribe(val => {
        this.form.controls['firstAccountingDateToExport'].setValidators(
          val === AccountingExportMethodKind.None ? null : Validators.required
        );
        this.form.controls['firstAccountingDateToExport'].updateValueAndValidity();
        if (val !== AccountingExportMethodKind.None) {
          setTimeout(() => this.form.controls['firstAccountingDateToExport'].markAsTouched(), 1);
        }
        this.updateFtp(
          val === AccountingExportMethodKind.SIE4Ftp ||
            val === AccountingExportMethodKind.AgressoGl07Ftp ||
            val === AccountingExportMethodKind.RaindanceFtp ||
            val === AccountingExportMethodKind.VismaRorFtp
        );
        this.updateEmailTransfer(val === AccountingExportMethodKind.SIE4Email);
        this.updateVismaControlEdge(val === AccountingExportMethodKind.VismaControlEdge);
        this.updateHogia(val);
      })
    );

    this.AddSubscription(
      this.form.controls['ftpProtocol'].valueChanges.subscribe(val => {
        if (val === 'SFTP' && this.form.controls['ftpPort'].value === 21) this.form.controls['ftpPort'].setValue(22);
        if (val === 'FTP' && this.form.controls['ftpPort'].value === 22) this.form.controls['ftpPort'].setValue(21);
      })
    );

    this.AddSubscription(
      this.form.controls['ftpFileFormat'].valueChanges.subscribe(val => {
        if (this.ftpEnabled) this.setAccountingTransferIntegrationType(this.getAccountingTransferIntegrationTypeForFtpFileFormat());
      })
    );

    this.AddSubscription(
      this.form.controls['accountingChoiceHogia'].valueChanges.subscribe(val => {
        this.updateHogiaAccountingText();
      })
    );

    this.AddSubscription(
      this.form.controls['accountingChoiceVisma'].valueChanges.subscribe(val => {
        this.updateVismaAccountingText();
      })
    );

    this.updateFtp(
      company.accountingTransferIntegrationType === AccountingExportMethodKind.SIE4Ftp ||
        company.accountingTransferIntegrationType === AccountingExportMethodKind.AgressoGl07Ftp ||
        company.accountingTransferIntegrationType === AccountingExportMethodKind.RaindanceFtp ||
        company.accountingTransferIntegrationType === AccountingExportMethodKind.VismaRorFtp
    );

    this.updateHogia(company.accountingTransferIntegrationType);
    this.updateVismaControlEdge(company.accountingTransferIntegrationType === AccountingExportMethodKind.VismaControlEdge);
    this.updateEmailTransfer(company.accountingTransferIntegrationType === AccountingExportMethodKind.SIE4Email);
  }

  private getCurrentAccountPlan(): AccountPlan | undefined {
    const accountPlanId = this.form?.controls['accountPlanId']?.value ?? this.dataContext?.accountPlanId;
    return this.accountPlans.find(plan => plan.id === accountPlanId);
  }

  get currentAccountPlanName(): string {
    return this.getCurrentAccountPlan()?.name ?? '';
  }

  private getCurrentDimensionLabels() {
    return this._dimensionLabelsService.fromAccountPlan(this.getCurrentAccountPlan());
  }

  private applyDimensionLabels() {
    const labels = this.getCurrentDimensionLabels();
    this.dimensionSelection = createDimensionSelection(labels);
    this.gl07DimensionSelection = createDimensionSelection(labels, true);
    this.customerOrSupplierIdDimSelection = [
      new SelectionItem('ACCOUNT_TRANSACTION.FIXED_VALUE', 99),
      new SelectionItem(labels.counterpartLabel, 4),
    ];
    this.companyCostPlaces?.controls.forEach(control =>
      this._dimensionLabelsService.setControlCaption(control.get('costPlace'), labels.costPlaceLabel)
    );
    this.companyCostBearers?.controls.forEach(control =>
      this._dimensionLabelsService.setControlCaption(control.get('costBearer'), labels.costBearerLabel)
    );
  }
}
